import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dialog } from 'primereact/dialog';
import Navbar from '../../../../common/navbar/Navbar';
import { apiCommon, apiCommonGet, baseUrl, vendorInvoice, getUserAllData } from '../../../../../Constants';
import InvoiceLoader from './InvoiceLoader';
import './invoice.css';
import './invoiceDashboard.css';
import { formatDate } from '../../../../../Utils/js/dateFormat';
import { Decoder } from '../../../../../Utils/js/Decoder';
import { showNotification } from '../../../../../Utils/js/notification.js';
import AttachmentTable from '../../../../../Utils/commonComponents/AttachmentTable';
import Swal from 'sweetalert2';
import JSONGrid from '@redheadphone/react-json-grid';
import { FaEye, FaList, FaPrint, FaSearchPlus, FaTruck } from 'react-icons/fa';
import { FiDownload, FiX } from 'react-icons/fi';
import { TbColumns, TbLayoutGridAdd } from 'react-icons/tb';

function InvoiceViewLegacy() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [lineAdjust, setLineAdjust] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [previewUrl, setPreviewUrl] = useState('');
  const [showPreview, setShowPreview] = useState(true);
  const [isPdf, setIsPdf] = useState(true);
  const [selectedLineIndex, setSelectedLineIndex] = useState(null);
  const userAllData = getUserAllData();
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (id) {
      fetchInvoiceData(id);
    }
  }, [id]);

  useEffect(() => () => {
    if (previewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
  }, [previewUrl]);

  const fetchInvoiceData = async (invoiceID) => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${baseUrl}${vendorInvoice}GetHeaderAndLineItemByID`,
        {
          InvoiceID: invoiceID,
          ObjCommon: {
            LoggedInUserID: userAllData[0]?.loginUserId,
            CompanyID: userAllData[0]?.companyId,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.status === 200) {
        const data = res.data?.responseDynamic ? Decoder(res.data.responseDynamic) : res.data;
        const header = data?.InvoiceHeader?.[0] || null;
        const invoiceLineItems = data?.InvoiceLineItem || [];
        const normalLineItems = invoiceLineItems.filter(item => parseFloat(item?.IsAdjustment || 0) !== 1);
        const adjustmentLineItems = invoiceLineItems.filter(item => parseFloat(item?.IsAdjustment || 0) === 1);
        setInvoiceData(header);
        setLineItems(normalLineItems);
        setLineAdjust(adjustmentLineItems);
        loadPreview(header?.OCRDocumentID);
      }
    } catch (error) {
      console.error('Error fetching invoice data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPreview = async (fileID) => {
    if (!fileID) {
      setPreviewUrl('');
      return;
    }

    try {
      const response = await fetch(`${baseUrl}${apiCommonGet}ViewDocument?FileID=${fileID}`, { method: 'GET' });
      const contentType = response.headers.get('Content-Type') || '';
      setIsPdf(contentType.toLowerCase().includes('pdf'));
      const blob = await response.blob();
      setPreviewUrl(URL.createObjectURL(blob));
      setShowPreview(true);
    } catch (error) {
      console.error('Error fetching invoice preview:', error);
      setPreviewUrl('');
    }
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '₹0.00';
    const num = parseFloat(value);
    return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    return formatDate(dateStr);
  };

  const value = (...keys) => {
    const found = keys.map(key => invoiceData?.[key]).find(item => item !== undefined && item !== null && item !== '');
    return found ?? '';
  };

  const lineValue = (item, ...keys) => {
    const found = keys.map(key => item?.[key]).find(data => data !== undefined && data !== null && data !== '');
    return found ?? '';
  };

  const display = (data, fallback = '---') => (data === 0 || data ? data : fallback);

  const selectedLineItem = selectedLineIndex !== null ? lineItems[selectedLineIndex] : null;

  if (loading) {
    return <InvoiceLoader />;
  }

  if (!invoiceData) {
    return (
      <div>
        <Navbar />
        <div className="container mt-5">
          <div className="alert alert-warning">Invoice not found</div>
          <button className="btn btn-primary" onClick={() => navigate(-1)}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Calculate totals
  const subTotal = lineItems.reduce((sum, item) => sum + parseFloat(item.AMOUNT || 0), 0);
  const taxAmount = lineItems.reduce((sum, item) => sum + parseFloat(item.TAX_AMOUNT_VENDOR || 0), 0);
  const lineAdjustTotal = lineAdjust.reduce((sum, item) => sum + parseFloat(item.AMOUNT || 0), 0);
  const tdsAmount = parseFloat(invoiceData.TDSAmount || 0);
  const grandTotal = subTotal + taxAmount + lineAdjustTotal - tdsAmount;

  return (
    <div className="invoice-view-page">
      <Navbar />
      <div className="container-fluid invoice-view-container">
        {/* Page Title and Actions */}
        <div className="d-flex justify-content-between align-items-center mb-3 invoice-view-titlebar">
          <h5 className="mb-0">Invoice Generation <span className="invoice-view-mode-badge">Draft Mode</span></h5>
          <div className="invoice-view-actions">
            <button type="button" className="btn btn-sm btn-success">Approve</button>
            <button type="button" className="btn btn-sm btn-outline-secondary">Review</button>
            <button type="button" className="btn btn-sm btn-outline-danger">Reject</button>
            <button type="button" className="btn btn-sm btn-outline-secondary">Hold</button>
            <button type="button" className="invoice-view-top-icon"><FaList /></button>
            <button type="button" className="invoice-view-top-icon">◎</button>
          </div>
        </div>

        <div className={showPreview ? 'invoice-view-layout has-preview' : 'invoice-view-layout no-preview'}>
          {showPreview && (
          <aside className="invoice-view-preview">
            <div className="invoice-view-preview-toolbar">
              <span className="preview-menu">☰</span>
              <span className="preview-page">1</span>
              <span>/ 1</span>
              <span className="preview-separator" />
              <span>-</span>
              <span>+</span>
              <span className="preview-spacer" />
              <FiDownload />
              <FaPrint />
            </div>
            <div className="invoice-view-preview-body">
              {previewUrl ? (
                isPdf ? <iframe title="Invoice Preview" src={previewUrl} /> : <img src={previewUrl} alt="Invoice Preview" />
              ) : (
                <div className="invoice-view-preview-empty">No document preview available</div>
              )}
            </div>
          </aside>
          )}

          <main className="invoice-view-main">
            {!showPreview && (
              <button type="button" className="invoice-open-preview-tab" onClick={() => setShowPreview(true)}>
                Open PDF Preview
              </button>
            )}

        {/* Header Card */}
        <div className="card mb-3" style={{ border: '1px solid #e0e0e0', borderRadius: '8px' }}>
          <div className="card-body p-3">
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                  Request Number
                </label>
                <div className="fw-semibold">{invoiceData.RequestNumber || '---'}</div>
              </div>
              <div className="col-md-3">
                <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                  Status
                </label>
                <div>
                  <span
                    className="badge"
                    style={{
                      background: invoiceData.Status === 'APPROVED' ? '#d4edda' : '#fff3cd',
                      color: invoiceData.Status === 'APPROVED' ? '#155724' : '#856404',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                    }}
                  >
                    {invoiceData.DisplayStatus || invoiceData.Status || '---'}
                  </span>
                </div>
              </div>
              <div className="col-md-3">
                <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                  Invoice Type
                </label>
                <div className="fw-semibold">{invoiceData.InvoiceType || '---'}</div>
              </div>
              <div className="col-md-3">
                <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                  Creation Date
                </label>
                <div className="fw-semibold">{formatDateDisplay(invoiceData.CreationDate)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Card */}
        <div className="card" style={{ border: '1px solid #e0e0e0', borderRadius: '8px' }}>
          <div className="card-body p-0">
            <TabView
              activeIndex={activeIndex}
              onTabChange={(e) => setActiveIndex(e.index)}
              style={{ border: 'none' }}
            >
              {/* Basic Details Tab */}
              <TabPanel header="Basic Details">
                <div className="p-3">
                  {/* Vendor and Invoice Info */}
                  <div className="row g-3 mb-4">
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Vendor Code
                      </label>
                      <div style={{ fontSize: '14px' }}>{invoiceData.VendorCode || '---'}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Vendor Name
                      </label>
                      <div style={{ fontSize: '14px' }}>{invoiceData.VendorName || '---'}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Invoice Date*
                      </label>
                      <div style={{ fontSize: '14px' }}>{formatDateDisplay(invoiceData.InvoiceDate)}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Invoice Number*
                      </label>
                      <div style={{ fontSize: '14px' }}>{invoiceData.InvoiceNumber || '---'}</div>
                    </div>
                  </div>

                  <div className="row g-3 mb-4">
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Reference Remarks
                      </label>
                      <div style={{ fontSize: '14px' }}>{invoiceData.ReferenceNumber || '---'}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        PO Number
                      </label>
                      <div style={{ fontSize: '14px', color: '#0084e8', cursor: 'pointer' }}>
                        {invoiceData.PONumber || '---'}
                      </div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Bill To
                      </label>
                      <div style={{ fontSize: '14px' }}>{invoiceData.BillToName || '---'}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Ship To
                      </label>
                      <div style={{ fontSize: '14px' }}>{invoiceData.ShipToName || '---'}</div>
                    </div>
                  </div>

                </div>
              </TabPanel>

              {/* Payment Details Tab */}
              <TabPanel header="Payment Details">
                <div className="p-3">
                  <div className="row g-3">
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Currency Symbol
                      </label>
                      <div style={{ fontSize: '14px' }}>{invoiceData.CurrencySymbol || '---'}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Net Payable
                      </label>
                      <div style={{ fontSize: '14px' }}>{formatCurrency(invoiceData.NetPayable)}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Payment Terms
                      </label>
                      <div style={{ fontSize: '14px' }}>{invoiceData.PaymentTerms || '---'}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Billing From
                      </label>
                      <div style={{ fontSize: '14px' }}>{formatDateDisplay(invoiceData.BillingFrom)}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Billing To
                      </label>
                      <div style={{ fontSize: '14px' }}>{formatDateDisplay(invoiceData.BillingTo)}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Bank Account Number
                      </label>
                      <div style={{ fontSize: '14px' }}>{invoiceData.BankAccountNo || '---'}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        IFSC Code
                      </label>
                      <div style={{ fontSize: '14px' }}>{invoiceData.IFSC || '---'}</div>
                    </div>
                  </div>
                </div>
              </TabPanel>

              {/* GST Details Tab */}
              <TabPanel header="GST Details">
                <div className="p-3">
                  <div className="row g-3">
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Supplier GSTIN
                      </label>
                      <div style={{ fontSize: '14px' }}>{invoiceData.SupplierGSTIN || '---'}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Source Of Supply
                      </label>
                      <div style={{ fontSize: '14px' }}>{invoiceData.SourceOfSupply || '---'}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Destination Supply
                      </label>
                      <div style={{ fontSize: '14px' }}>{invoiceData.DestinationSupply || '---'}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Original Invoice Reference
                      </label>
                      <div style={{ fontSize: '14px' }}>{invoiceData.OriginalInvoiceReference || '---'}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        IRN Code
                      </label>
                      <div style={{ fontSize: '14px' }}>{invoiceData.IRNCode || '---'}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        E-Invoice
                      </label>
                      <div style={{ fontSize: '14px' }}>{invoiceData.EInvoice === '1' ? 'Yes' : 'No'}</div>
                    </div>
                  </div>
                </div>
              </TabPanel>

              {/* Office Use Tab */}
              <TabPanel header="Office Use">
                <div className="p-3">
                  <div className="row g-3">
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Department
                      </label>
                      <div style={{ fontSize: '14px' }}>{invoiceData.DepartmentName || '---'}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Cost Center
                      </label>
                      <div style={{ fontSize: '14px' }}>{invoiceData.CostCenterCode || '---'}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        GL Code
                      </label>
                      <div style={{ fontSize: '14px' }}>{invoiceData.GLCode || '---'}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Accounting Date
                      </label>
                      <div style={{ fontSize: '14px' }}>{formatDateDisplay(invoiceData.AccountingDate)}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Company Code
                      </label>
                      <div style={{ fontSize: '14px' }}>{invoiceData.CompanyCode || '---'}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Plant Code
                      </label>
                      <div style={{ fontSize: '14px' }}>{invoiceData.PlantCode || '---'}</div>
                    </div>
                  </div>
                </div>
              </TabPanel>

              {/* General Info Tab */}
              <TabPanel header="General Info">
                <div className="p-3">
                  <div className="row g-3">
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Text1
                      </label>
                      <div style={{ fontSize: '14px' }}>{invoiceData.Text1 || '---'}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Text2
                      </label>
                      <div style={{ fontSize: '14px' }}>{invoiceData.Text2 || '---'}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Text3
                      </label>
                      <div style={{ fontSize: '14px' }}>{invoiceData.Text3 || '---'}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Text4
                      </label>
                      <div style={{ fontSize: '14px' }}>{invoiceData.Text4 || '---'}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Date1
                      </label>
                      <div style={{ fontSize: '14px' }}>{formatDateDisplay(invoiceData.Date1)}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Date2
                      </label>
                      <div style={{ fontSize: '14px' }}>{formatDateDisplay(invoiceData.Date2)}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Date3
                      </label>
                      <div style={{ fontSize: '14px' }}>{formatDateDisplay(invoiceData.Date3)}</div>
                    </div>
                  </div>
                </div>
              </TabPanel>

              {/* Super Admin Rights Tab */}
              <TabPanel header="Super Admin Rights">
                <div className="p-3">
                  <div className="row g-3">
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Alternate Approval
                      </label>
                      <div style={{ fontSize: '14px' }}>{invoiceData.AltApproval || '---'}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Receipt Date
                      </label>
                      <div style={{ fontSize: '14px' }}>{formatDateDisplay(invoiceData.ReceiptDate)}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Next Approver
                      </label>
                      <div style={{ fontSize: '14px' }}>{invoiceData.NextApprover || '---'}</div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>
                        Actual Status
                      </label>
                      <div style={{ fontSize: '14px' }}>{invoiceData.ActualStatus || '---'}</div>
                    </div>
                  </div>
                </div>
              </TabPanel>
            </TabView>
          </div>
        </div>

            <section className="invoice-view-screen-card invoice-details-screen">
              <h5>Invoice Details</h5>
              <div className="invoice-screen-table-wrap">
                <table className="invoice-screen-table">
                  <thead>
                    <tr>
                      <th style={{ width: '7%' }}>S.No</th>
                      <th style={{ width: '14%' }}>Material Code</th>
                      <th>Material Description</th>
                      <th>Description</th>
                      <th style={{ width: '13%' }}>Quantity<span>*</span></th>
                      <th style={{ width: '15%' }}>Quantity Object</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.length > 0 ? (
                      lineItems.map((item, index) => (
                        <tr
                          key={lineValue(item, 'InvoiceLineItemID', 'LineItemID') || index}
                          className="invoice-line-clickable-row"
                          onClick={() => setSelectedLineIndex(index)}
                        >
                          <td>{index + 1}</td>
                          <td><div className="screen-readonly-cell">{lineValue(item, 'MATERIAL_CODE', 'MaterialCode')}</div></td>
                          <td><div className="screen-readonly-cell text-cell">{lineValue(item, 'DESCRIPTION', 'MaterialDescription', 'SHORT_TEXT')}</div></td>
                          <td><div className="screen-readonly-cell text-cell">{lineValue(item, 'Description', 'ITEM_TEXT')}</div></td>
                          <td className="screen-qty-cell">{display(lineValue(item, 'QTY', 'Quantity'), 0)}</td>
                          <td>
                            <button
                              type="button"
                              className="invoice-view-line-btn"
                              title="Line Item Details"
                              onClick={(event) => {
                                event.stopPropagation();
                                setSelectedLineIndex(index);
                              }}
                            >
                              <FaSearchPlus />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="empty-table">No data available</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="invoice-view-footer-row">
              <div className="invoice-view-screen-card invoice-tds-screen">
                <h6>Tax Deductions (TDS)</h6>
                <div className="invoice-tds-grid">
                  <div>
                    <label>TDS Tax Code</label>
                    <span>{invoiceData.TDSCode || 'Select...'}</span>
                  </div>
                  <div>
                    <label>TDS Percent (%)</label>
                    <span>{invoiceData.TDSPercent || '0.00'}</span>
                  </div>
                  <div>
                    <label>TDS Amount</label>
                    <span>{formatCurrency(invoiceData.TDSAmount || 0)}</span>
                  </div>
                </div>
              </div>
              <div className="invoice-view-screen-card invoice-summary-screen">
                <div><span>Sub Total</span><b>{formatCurrency(subTotal)}</b></div>
                <div><span>Tax Amount</span><b>{formatCurrency(taxAmount)}</b></div>
                <div><span>Adjustments</span><b>{formatCurrency(lineAdjustTotal)}</b></div>
                <div className="grand"><span>Grand Total</span><b>{formatCurrency(grandTotal)}</b></div>
              </div>
            </section>
          </main>
        </div>

        {/* Bottom Info */}
        <div className="mt-3" style={{ fontSize: '12px', color: '#888' }}>
          Last updated: {formatDateDisplay(invoiceData.LastModifiedDate)} | User: {invoiceData.LastModifiedBy || 'System'}
        </div>
      </div>

      {selectedLineItem && (
        <div className="invoice-line-drawer-overlay">
          <div className="invoice-line-drawer-backdrop" onClick={() => setSelectedLineIndex(null)} />
          <aside className="invoice-line-drawer">
            <div className="invoice-line-drawer-head">
              <div>
                <h5>Line Item Details</h5>
                {/* <span>Granular view - Item ID: {lineValue(selectedLineItem, 'InvoiceLineItemID', 'LineItemID') || selectedLineIndex + 1}</span> */}
              </div>
              <button type="button" onClick={() => setSelectedLineIndex(null)}><FiX /></button>
            </div>
            <div className="invoice-line-drawer-grid">
              <div><label>Line Number</label><span>{selectedLineIndex + 1}</span></div>
              <div><label>Material ID</label><span>{display(lineValue(selectedLineItem, 'MATERIAL_CODE', 'MaterialCode'))}</span></div>
              <div><label>PO Number</label><span>{display(lineValue(selectedLineItem, 'PO_NO', 'PONumber'))}</span></div>
              <div><label>PO Item</label><span>{display(lineValue(selectedLineItem, 'PO_ITEM', 'POItem'))}</span></div>
              <div><label>Ext Row</label><span>{display(lineValue(selectedLineItem, 'EXT_ROW', 'ExtRow'), 0)}</span></div>
              <div className="wide"><label>Description</label><span>{display(lineValue(selectedLineItem, 'DESCRIPTION', 'MaterialDescription', 'SHORT_TEXT'))}</span></div>
              <div><label>Quantity</label><span>{display(lineValue(selectedLineItem, 'QTY', 'Quantity'), 0)}</span></div>
              <div><label>Unit Of Measure</label><span>{display(lineValue(selectedLineItem, 'UOM', 'UnitOfMeasure'))}</span></div>
              <div><label>Unit Rate</label><span>{formatCurrency(lineValue(selectedLineItem, 'RATE', 'UnitRate'))}</span></div>
              <div><label>Taxable Amount</label><span>{formatCurrency(lineValue(selectedLineItem, 'AMOUNT', 'TAXABLE_AMOUNT', 'TaxableAmount'))}</span></div>
              <div><label>Tax Component</label><span>{formatCurrency(lineValue(selectedLineItem, 'TAX_AMOUNT_VENDOR', 'TAX_AMOUNT', 'TaxAmount'))}</span></div>
              <div><label>Total Gross</label><span>{formatCurrency(lineValue(selectedLineItem, 'GROSS_AMOUNT', 'GrossAmount'))}</span></div>
              <div><label>HSN Mapping</label><span>{display(lineValue(selectedLineItem, 'HSN_CODE', 'HSNCode'))}</span></div>
            </div>
            <div className="invoice-line-drawer-footer">
              <span>Line Item {selectedLineIndex + 1} of {lineItems.length}</span>
              <div>
                <button type="button" disabled={selectedLineIndex === 0} onClick={() => setSelectedLineIndex(index => Math.max(0, index - 1))}>Previous</button>
                <button type="button" disabled={selectedLineIndex === lineItems.length - 1} onClick={() => setSelectedLineIndex(index => Math.min(lineItems.length - 1, index + 1))}>Next</button>
              </div>
            </div>
            <button type="button" className="invoice-line-dismiss" onClick={() => setSelectedLineIndex(null)}>Dismiss</button>
          </aside>
        </div>
      )}
    </div>
  );
}

export default function InvoiceView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [lineAdjust, setLineAdjust] = useState([]);
  const [checkListRows, setCheckListRows] = useState([]);
  const [vendorPerformanceRows, setVendorPerformanceRows] = useState([]);
  const [poLineRows, setPoLineRows] = useState([]);
  const [invoiceWithPoRows, setInvoiceWithPoRows] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [previewUrl, setPreviewUrl] = useState('');
  const [showPreview, setShowPreview] = useState(true);
  const [isPdf, setIsPdf] = useState(true);
  const [selectedLineIndex, setSelectedLineIndex] = useState(null);
  const [selectedLineSource, setSelectedLineSource] = useState('invoice');
  const [ocrjsonData, setOcrjsonData] = useState(null);
  const [jsonDialog, setJsonDialog] = useState(false);
  const [valData, setValData] = useState(null);
  const [visibleTotalLineItems, setVisibleTotalLineItems] = useState(null);
  const [dropdownData, setDropdownData] = useState({});
  const [linePopup, setLinePopup] = useState({
    visible: false,
    title: '',
    rows: [],
  });
  const [reportLoading, setReportLoading] = useState(false);
  const [approvalHistoryVisible, setApprovalHistoryVisible] = useState(false);
  const [approvalHistoryLoading, setApprovalHistoryLoading] = useState(false);
  const [approvalHistoryRows, setApprovalHistoryRows] = useState([]);
  const [takeActionMenuOpen, setTakeActionMenuOpen] = useState(false);
  const [takeActionVisible, setTakeActionVisible] = useState(false);
  const [takeActionLoading, setTakeActionLoading] = useState(false);
  const [selectedViewAction, setSelectedViewAction] = useState(null);
  const [actionRemarks, setActionRemarks] = useState('');
  const [reviewResetOption, setReviewResetOption] = useState(0);
  const [reviewEmailType, setReviewEmailType] = useState('');
  const [reviewEmailDialog, setReviewEmailDialog] = useState(false);
  const [reviewEmailRows, setReviewEmailRows] = useState([]);
  const [reviewUserRows, setReviewUserRows] = useState([]);
  const [reviewUserLoading, setReviewUserLoading] = useState(false);
  const [reviewUserSearch, setReviewUserSearch] = useState('');
  const [reviewUserPage, setReviewUserPage] = useState(1);
  const [reviewUserLimit, setReviewUserLimit] = useState(10);
  const [reviewUserTotalPages, setReviewUserTotalPages] = useState(0);
  const [reportDialog, setReportDialog] = useState({
    visible: false,
    title: '',
    action: '',
    data: [],
    page: 1,
    limit: 200,
    search: '',
    totalPages: 0,
    totalRecords: 0,
  });
  const userAllData = getUserAllData();
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (id) fetchInvoiceData(id);
  }, [id]);

  useEffect(() => () => {
    if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const fetchInvoiceData = async (invoiceID) => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${baseUrl}${vendorInvoice}GetHeaderAndLineItemByID`,
        {
          InvoiceID: invoiceID,
          ObjCommon: {
            LoggedInUserID: userAllData[0]?.loginUserId,
            CompanyID: userAllData[0]?.companyId,
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.status === 200) {
        const data = res.data?.responseDynamic ? Decoder(res.data.responseDynamic) : res.data;
        const header = data?.InvoiceHeader?.[0] || null;
        const invoiceLineItems = data?.InvoiceLineItem || [];
        const normalLineItems = invoiceLineItems.filter(item => parseFloat(item?.IsAdjustment || 0) !== 1);
        const adjustmentLineItems = invoiceLineItems.filter(item => parseFloat(item?.IsAdjustment || 0) === 1);
        setInvoiceData(header);
        setLineItems(normalLineItems);
        setLineAdjust(adjustmentLineItems);
        setCheckListRows(data?.InvoiceChecklist || data?.CheckList || data?.VendorCheckList || []);
        setVendorPerformanceRows(data?.VendorPerformanceRating || data?.VendorPerformanceList || data?.InvoiceVendorPerformanceRating || []);
        setPoLineRows(data?.MappedPOLineItem || data?.POLineItems || data?.POLineItem || []);
        setInvoiceWithPoRows(data?.InvoiceWithThisPO || data?.InvoicesWithThisPO || data?.POInvoiceList || []);
        fieldValidationService(header);
        dropdownService(header, invoiceID);
        try {
          setOcrjsonData(header?.OCRJSONData ? JSON.parse(header.OCRJSONData) : null);
        } catch (err) {
          console.log(err);
          setOcrjsonData(null);
        }
        loadPreview(header?.OCRDocumentID);
      }
    } catch (error) {
      console.error('Error fetching invoice data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fieldValidationService = async (header) => {
    try {
      const res = await axios.post(
        `${baseUrl}${apiCommon}GetFormFieldValidation`,
        {
          FormName: 'Invoice',
          StatusName: `${header?.Status ?? ''}`,
          TransType: `${header?.TransType ?? ''}`,
          TransNumber: `${header?.RequestNumber ?? ''}`,
          IsVendor: userAllData[0]?.isVendor || '',
          ObjCommon: {
            LoggedInUserID: userAllData[0]?.loginUserId,
            CompanyID: userAllData[0]?.companyId,
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const dt = res.data;
      if (res.status === 200 && dt?.responseCode === '1' && dt?.responseDynamic !== '') {
        const encryptData = Decoder(dt?.responseDynamic);
        const formControl = encryptData?.GetFormFieldValidation?.map(item => (
          item.ValidationSet ? JSON.parse(item.ValidationSet) : null
        )) || [];
        const firstObject = formControl?.[0] || {};
        setValData(formControl);
        setVisibleTotalLineItems(
          Object.entries(firstObject)
            .filter(([key, value]) => key.startsWith('TH_') && value?.visible === 1 && value?.controlType === 'TableColumn')
            .map(([key]) => key.trim())
        );
      } else if (res.status === 200 && dt?.responseCode === '1' && dt?.responseDynamic === '') {
        setValData(null);
        setVisibleTotalLineItems([
          'TH_AMT', 'TH_ASSET', 'TH_CST_CNTR', 'TH_DESC_CODE', 'TH_DESCRIPTION',
          'TH_DIS_AMT', 'TH_DIS_PER', 'TH_EXT_ROW', 'TH_GL_CODE', 'TH_GR_AMT', 'TH_HSN_CODE',
          'TH_MATERIAL_CODE', 'TH_MATERIAL_DESCRIPTION', 'TH_OTHR_CHRG', 'TH_PO_ITEM',
          'TH_PO_NO', 'TH_PROJECT', 'TH_QTY', 'TH_RATE', 'TH_TX_AMT',
          'TH_TX_AMT_CAL', 'TH_TX_AMT_CAL_PER', 'TH_TX_CODE', 'TH_UOM', 'TH_QTY_OBJECT', 'TH_TAX_OBJECT',
        ]);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const getDropdownOptions = async (header, key, miscParamID = '') => {
    const actionName =
      key === 'VENDOR'
        ? 'GET_ALL_SELECT_INVOICE_PARAM'
        : key === 'INVOICE'
          ? 'GET_ALL_SELECT_INVOICE_ALT_APPROVAL'
          : 'GET_ALL_SELECT_INVOICE';

    const response = await axios.post(
      `${baseUrl}${apiCommon}GetCommonDropdown`,
      {
        IsVendor: userAllData[0]?.isVendor || '',
        MISCParamID: `${miscParamID ?? ''}`,
        ObjCommon: {
          Action: actionName,
          CompanyID: userAllData[0]?.companyId,
          LoggedInUserID: userAllData[0]?.loginUserId,
        },
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const dt = response.data;
    if (response.status !== 200 || dt?.responseCode !== '1') return {};

    const encryptData = Decoder(dt?.responseDynamic);
    const selectOptions = [
      'INVOICEBILLTO',
      'INVOICESHIPTO',
      'PAYMENTTERMS',
      'INVOICESUBTYPE',
      'INVOICEPRIORITY',
      'GLACCOUNT',
      'INVOICECOMPANY',
      'INVOICECOSTCENTER',
      'INVOICEDEPARTMENT',
      'INVOICEPLANT',
      'VEHICLETYPE',
      'INVOICEUOM',
      'INVOICEBANKACCOUNT',
      'INVOICEGSTIN',
      'INVOICETDSTAXCODE',
      'INVOICEALTERNATEAPPROVAL',
      'INVOICECURRENCY',
      'INVOICECRITICALITY',
      'INVOICEMATERIALCATEGORY',
      'INVOICEPO_PRCREATOR',
      'CHARGETYPE',
      'INVOICERATING',
    ];
    const updatedDropdownData = {};
    selectOptions.forEach(selectType => {
      const filterData = (encryptData?.Table || [])
        .filter(item => item.SelectType === selectType)
        .map(item => ({
          name: item?.text,
          code: item?.ID,
          keyCode: item?.KeyCode,
          ...(selectType === 'INVOICETDSTAXCODE' && {
            AmountType: item?.AmountType,
            Tag1: item?.Tag1,
            Tag2: item?.Tag2,
          }),
        }));
      updatedDropdownData[selectType] = [{ name: 'Select...', code: 0, keyCode: '' }, ...filterData];
    });

    if (key === 'VENDOR') {
      return {
        INVOICEBANKACCOUNT: updatedDropdownData.INVOICEBANKACCOUNT,
        INVOICEGSTIN: updatedDropdownData.INVOICEGSTIN,
        INVOICETDSTAXCODE: updatedDropdownData.INVOICETDSTAXCODE,
      };
    }

    if (key === 'INVOICE') {
      return {
        INVOICEALTERNATEAPPROVAL: updatedDropdownData.INVOICEALTERNATEAPPROVAL,
      };
    }

    return updatedDropdownData;
  };

  const dropdownService = async (header, invoiceID) => {
    try {
      const [commonDropdowns, invoiceDropdowns, vendorDropdowns] = await Promise.all([
        getDropdownOptions(header, ''),
        getDropdownOptions(header, 'INVOICE', header?.ID || invoiceID || ''),
        header?.SupplierNumber ? getDropdownOptions(header, 'VENDOR', header.SupplierNumber) : Promise.resolve({}),
      ]);
      setDropdownData(prev => ({
        ...prev,
        ...commonDropdowns,
        ...invoiceDropdowns,
        ...vendorDropdowns,
      }));
    } catch (err) {
      console.log(err);
    }
  };

  const loadPreview = async (fileID) => {
    if (!fileID) {
      setPreviewUrl('');
      return;
    }

    try {
      const response = await fetch(`${baseUrl}${apiCommonGet}ViewDocument?FileID=${fileID}`, { method: 'GET' });
      setIsPdf((response.headers.get('Content-Type') || '').toLowerCase().includes('pdf'));
      const blob = await response.blob();
      setPreviewUrl(URL.createObjectURL(blob));
      setShowPreview(true);
    } catch (error) {
      console.error('Error fetching invoice preview:', error);
      setPreviewUrl('');
    }
  };

  const downloadPreviewDocument = () => {
    if (!previewUrl) return;
    const link = document.createElement('a');
    link.href = previewUrl;
    link.download = value('InvoiceDocumentName', 'OCRDocumentName') || 'invoice.pdf';
    link.click();
  };

  const printPreviewDocument = () => {
    if (!previewUrl) return;
    const printWindow = window.open(previewUrl, '_blank');
    if (printWindow) {
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 500);
    }
  };

  const getVendorInvoicePrintData = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await axios.post(
        `${baseUrl}${vendorInvoice}InvoicePrintData`,
        {
          InvoiceID: `${id}`,
          ObjCommon: {
            LoggedInUserID: userAllData[0]?.loginUserId,
            CompanyID: userAllData[0]?.companyId,
          },
          InvCommon: {
            IsVendor: `${userAllData[0]?.isVendor}`,
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const dt = res.data;
      if (res.status === 200 && dt.responseCode === '1') {
        const encryptData = Decoder(dt?.responseDynamic);
        navigate('/Vendor/Invoice/Print', {
          state: {
            printData: {
              InvoiceHeader: encryptData?.InvoiceHeader?.[0],
              InvoiceLineItem: encryptData?.InvoiceLineItem,
              InvoiceLineItemAmount: encryptData?.InvoiceLineItemAmount?.[0],
              InvoiceWorkFlowCreationDate: encryptData?.InvoiceWorkFlowCreationDate,
              InvoiceWorkflowLog: encryptData?.InvoiceWorkflowLog,
              InvoiceChecklist: encryptData?.InvoiceChecklist,
              InvoiceDMS: encryptData?.InvoiceDMS,
            },
          },
        });
      } else {
        showNotification('toast-error', dt?.responseMessage || 'Print data not found');
      }
    } catch (err) {
      console.log(err);
      showNotification('toast-error', 'Unable to fetch print data');
    } finally {
      setLoading(false);
    }
  };

  const pushToSapDataShow = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
    });
    if (!result.isConfirmed) return;

    setLoading(true);
    try {
      const res = await axios.post(
        `${baseUrl}${vendorInvoice}InvoicePushToSAP`,
        {
          InvoiceID: `${value('InvoiceID') || id}`,
          IsVendor: `${userAllData[0]?.isVendor || ''}`,
          RequestNo: `${value('RequestNumber')}`,
          ObjCommon: {
            LoggedInUserID: userAllData[0]?.loginUserId,
            CompanyID: userAllData[0]?.companyId,
          },
          InvCommon: {
            IsVendor: `${userAllData[0]?.isVendor}`,
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const dt = res.data;
      if (res.status === 200 && dt?.responseCode === '1') {
        showNotification('swal-success', dt?.responseMessage);
      } else {
        showNotification('toast-error', dt?.responseMessage || 'Push To SAP failed');
      }
    } catch (err) {
      console.log(err);
      showNotification('toast-error', 'Push To SAP failed');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '\u20b90.00';
    const num = parseFloat(value);
    if (Number.isNaN(num)) return '\u20b90.00';
    return `\u20b9${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDateDisplay = (dateStr) => (dateStr ? formatDate(dateStr) : '');
  const formatDateOnlyDisplay = (dateStr) => {
    const formattedDate = formatDateDisplay(dateStr);
    return formattedDate ? `${formattedDate}`.split(' ')[0] : '';
  };
  const value = (...keys) => keys.map(key => invoiceData?.[key]).find(item => item !== undefined && item !== null && item !== '') ?? '';
  const lineValue = (item, ...keys) => keys.map(key => item?.[key]).find(data => data !== undefined && data !== null && data !== '') ?? '';
  const display = (data, fallback = '---') => (data === 0 || data ? data : fallback);
  const sameValue = (left, right) => `${left ?? ''}` === `${right ?? ''}`;
  const resolveDropdown = (options, rawValue, fallback = '') => {
    const cleanFallback = fallback && fallback !== 'Select...' ? fallback : '';
    if (rawValue === undefined || rawValue === null || rawValue === '' || rawValue === 0 || rawValue === '0') {
      return cleanFallback;
    }
    const selected = (options || []).find(option => sameValue(option?.code, rawValue) || sameValue(option?.keyCode, rawValue) || sameValue(option?.name, rawValue));
    if (selected?.name && selected.name !== 'Select...') return selected.name;
    return cleanFallback || rawValue;
  };
  const dropdownLabel = (selectType, rawValue, fallback = '') => resolveDropdown(dropdownData?.[selectType], rawValue, fallback);
  const selectedLineList = selectedLineSource === 'adjustment' ? lineAdjust : lineItems;
  const selectedLineItem = selectedLineIndex !== null ? selectedLineList[selectedLineIndex] : null;
  const openLineDrawer = (index, source = 'invoice') => {
    setSelectedLineSource(source);
    setSelectedLineIndex(index);
  };
  const closeLineDrawer = () => setSelectedLineIndex(null);
  const canShowPushToSap = (
    value('Status')?.toUpperCase() === 'DRAFT' &&
    value('IsDelete') !== 1 &&
    Number(value('ShowPushToSAPButton')) === 1
  ) || (
    value('Status')?.toUpperCase() === 'FINAL' &&
    value('IsDelete') !== 1
  );
  const ocrDocumentID = Boolean(value('OCRDocumentID'));

  const tdsAmount = parseFloat(invoiceData?.TDSAmount || 0);
  const validationSet = valData?.[0] || null;
  const isVisible = key => !validationSet || !validationSet[key] || validationSet[key]?.visible === 1;

  const tooltipValue = (data, fallback) => {
    const tooltipData = data === 0 || data ? data : fallback;
    const tooltipText = tooltipData === 0 || tooltipData ? String(tooltipData) : '';
    return tooltipText === '---' ? '' : tooltipText;
  };

  const Field = ({ label, children, className = 'col-md-2', numeric = false, fieldKey, apiTooltip }) => {
    if (fieldKey && !isVisible(fieldKey)) return null;
    const fieldTooltip = tooltipValue(apiTooltip, children);

    return (
      <div className={className}>
        <div className="input-form-group invoice-view-field">
          <label className="input-label">{label}</label>
          <div
            className={`invoice-view-readonly ${numeric ? 'numeric' : ''} ${fieldTooltip ? 'invoice-view-tooltip-anchor' : ''}`}
            title={fieldTooltip}
            data-tooltip={fieldTooltip}
          >
            <span className="invoice-view-readonly-text">{display(children)}</span>
          </div>
        </div>
      </div>
    );
  };

  const SectionFields = ({ children, className = 'invoice-detail-section' }) => (
    <div className="matCon-card">
      <div className={className}>
        <div className="row mt-0">{children}</div>
      </div>
    </div>
  );

  const renderFields = fields => fields.map(([label, fieldValue, fieldKey, apiTooltip]) => (
    <Field key={label} label={label} fieldKey={fieldKey} apiTooltip={apiTooltip}>{fieldValue}</Field>
  ));

  const comparisionReport = async (page, limit, search, action) => {
    const reportMap = {
      COMP_REPORT: {
        title: 'Comparison Report',
        reportType: 'COMPARISION_REPORT',
        url: 'GetInvoiceReport',
      },
      GRN_REPORT: {
        title: 'GRN Report',
        reportType: 'GRN_REPORT',
        url: 'GetGRNDetailsByInvoice',
      },
    };
    const config = reportMap[action];
    if (!config) return;

    setReportLoading(true);
    try {
      const response = await axios.post(
        `${baseUrl}${vendorInvoice}${config.url}`,
        {
          ...(config.reportType === 'COMPARISION_REPORT' && {
            ReportType: config.reportType,
            RequestNo: `${value('RequestNumber') || ''}`,
            VendorID: '',
            CreationDate_From: '',
            CreationDate_To: '',
            InvoiceDate_From: '',
            InvoiceDate_To: '',
            Status: '',
            IsFinalApproved: '',
            CostCenter_From: '',
            CostCenter_To: '',
            GLCode_From: '',
            GLCode_To: '',
            BillTo_From: '',
            BillTo_To: '',
            ShipTo_From: '',
            ShipTo_To: '',
            InvoiceNumber: '',
            PONumber: '',
            BillingFrom_From: '',
            BillingFrom_To: '',
            BillingTo_From: '',
            BillingTo_To: '',
            InvoiceSubTypeID: '',
            PlantID: '',
            UserID: userAllData[0]?.userId,
          }),
          ...(config.reportType === 'GRN_REPORT' && {
            InvoiceID: `${value('InvoiceID') || id || ''}`,
          }),
          ObjCommon: {
            CompanyID: userAllData[0]?.companyId,
            LoggedInUserID: userAllData[0]?.loginUserId,
            PageIndex: `${page || ''}`,
            PageSize: `${limit || ''}`,
            SearchText: `${search || ''}`,
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const dt = response.data;
      if (response.status === 200 && dt?.responseCode === '1' && dt?.filterCount !== 0) {
        const encryptData = Decoder(dt?.responseDynamic);
        setReportDialog(prev => ({
          ...prev,
          data: encryptData?.[config.url] || [],
          page,
          limit,
          search,
          totalPages: Math.ceil(dt?.filterCount / limit),
          totalRecords: dt?.filterCount,
        }));
      } else if (response.status === 200 && dt?.responseCode === '1' && dt?.filterCount === 0) {
        setReportDialog(prev => ({
          ...prev,
          data: [],
          page,
          limit,
          search,
          totalPages: 0,
          totalRecords: 0,
        }));
      } else {
        showNotification('toast-error', dt?.responseMessage || 'Report data not found');
      }
    } catch (err) {
      console.log(err);
      showNotification('toast-error', 'Unable to fetch report data');
    } finally {
      setReportLoading(false);
    }
  };

  const openReportDialog = (action) => {
    const title = action === 'COMP_REPORT' ? 'Comparison Report' : 'GRN Report';
    setReportDialog({
      visible: true,
      title,
      action,
      data: [],
      page: 1,
      limit: 200,
      search: '',
      totalPages: 0,
      totalRecords: 0,
    });
    comparisionReport(1, 200, '', action);
  };

  const fetchApprovalHistory = async () => {
    setApprovalHistoryVisible(true);
    setApprovalHistoryLoading(true);
    try {
      const res = await axios.post(
        `${baseUrl}${vendorInvoice}GetInvoiceWorkflow`,
        {
          InvoiceID: `${value('InvoiceID') || id || ''}`,
          ObjCommon: {
            LoggedInUserID: userAllData[0]?.loginUserId,
            SearchText: '',
            CompanyID: userAllData[0]?.companyId,
            PageIndex: '1',
            PageSize: '200',
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const dt = res.data;
      if (res.status === 200 && dt?.responseCode === '1' && dt?.filterCount !== 0) {
        const encryptData = Decoder(dt?.responseDynamic);
        setApprovalHistoryRows(encryptData?.GetInvoiceWorkflow || []);
      } else {
        setApprovalHistoryRows([]);
      }
    } catch (err) {
      console.log(err);
      setApprovalHistoryRows([]);
    } finally {
      setApprovalHistoryLoading(false);
    }
  };

  const submitViewAction = async (status, remarks = '') => {
    setTakeActionLoading(true);
    try {
      const res = await axios.post(
        `${baseUrl}${vendorInvoice}InvoiceApprovalByInvoiceID`,
        {
          InvoiceHeader: {
            ...invoiceData,
            InvoiceID: `${value('InvoiceID') || id || ''}`,
            CompanyID: userAllData[0]?.companyId,
            RequestNumber: `${value('RequestNumber') || ''}`,
            TransType: `${value('TransType') || ''}`,
            InvoiceType: `${value('InvoiceType') || ''}`,
            Status: status,
            IsVendor: `${value('IsVendor') || userAllData[0]?.isVendor || ''}`,
            Remarks: remarks,
          },
          LstInvoiceLineItem: [...lineItems, ...lineAdjust],
          ObjCommon: {
            LoggedInUserID: userAllData[0]?.loginUserId,
            CompanyID: userAllData[0]?.companyId,
            DateShort: userAllData[0]?.dateShort,
          },
          InvCommon: {
            IsVendor: `${userAllData[0]?.isVendor}`,
            AllowApprovalSave: value('IsVendor') === 1 && userAllData[0]?.isVendor === '0' && ['SUBMIT', 'APH001', 'APRW1'].includes(value('Status')?.toUpperCase()) ? '1' : '0',
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const dt = res.data;
      if (res.status === 200 && dt?.responseCode === '1') {
        showNotification('swal-success', dt?.responseMessage || 'Invoice action updated successfully');
        setTakeActionVisible(false);
        setSelectedViewAction(null);
        setActionRemarks('');
        fetchInvoiceData(id);
      } else {
        showNotification('toast-error', dt?.responseMessage || 'Unable to update invoice action');
      }
    } catch (err) {
      console.log(err);
      showNotification('toast-error', 'Unable to update invoice action');
    } finally {
      setTakeActionLoading(false);
    }
  };

  const actionOptions = [
    { status: 'APPROVED', label: 'Approve', menuLabel: 'Approve', icon: 'pi-check-circle', tone: 'approve', help: 'This invoice will be approved and moved to the next level.', remarksLabel: 'Approved Remarks', placeholder: 'Enter reason for update...' },
    { status: 'HOLD', label: 'Hold', menuLabel: 'Hold', icon: 'pi-pause-circle', tone: 'hold', help: 'This invoice will be kept on hold with your remarks.', remarksLabel: 'Hold Remarks', placeholder: 'Enter hold reason...' },
    { status: 'REJECTED', label: 'Reject', menuLabel: 'Reject', icon: 'pi-times', tone: 'reject', help: 'This invoice will be rejected with your remarks.', remarksLabel: 'Rejected Remarks', placeholder: 'Enter rejection reason...' },
    { status: 'REVIEWED', label: 'In Review', menuLabel: 'Send for review', icon: 'pi-users', tone: 'review', help: 'The process will be sent for review. It will not move to next level.' },
  ];

  const openViewAction = (action) => {
    setSelectedViewAction(action);
    setActionRemarks('');
    setTakeActionMenuOpen(false);
    setTakeActionVisible(true);
  };

  const ccBccListService = async (page = 1, limit = reviewUserLimit, search = '') => {
    setReviewUserLoading(true);
    try {
      const res = await axios.post(
        `${baseUrl}${apiCommon}GetCommonSearchHelp`,
        {
          SearchTerm: search,
          MISCParam: '',
          VendorID: userAllData[0]?.isVendor,
          IsVendor: '0',
          ObjCommon: {
            LoggedInUserID: userAllData[0]?.loginUserId,
            Action: 'GET_USER_DATA_PAGGED',
            PageIndex: `${page}`,
            PageSize: `${limit}`,
            CompanyID: userAllData[0]?.companyId,
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const dt = res.data;
      if (res.status === 200 && dt?.responseCode === '1' && dt?.filterCount !== 0) {
        const encryptData = Decoder(dt?.responseDynamic);
        setReviewUserRows(encryptData?.GetCommonSearchHelp || []);
        setReviewUserTotalPages(Math.ceil((dt?.filterCount || 0) / Number(limit || 10)));
      } else if (res.status === 200 && dt?.responseCode === '1' && dt?.filterCount === 0) {
        setReviewUserRows([]);
        setReviewUserTotalPages(0);
      } else {
        setReviewUserRows([]);
        setReviewUserTotalPages(0);
        if (dt?.responseMessage) showNotification('toast-error', dt.responseMessage);
      }
    } catch (err) {
      console.log(err);
      setReviewUserRows([]);
      setReviewUserTotalPages(0);
    } finally {
      setReviewUserLoading(false);
    }
  };

  const openReviewEmailDialog = (type) => {
    setReviewEmailType(type);
    setReviewUserPage(1);
    setReviewUserLimit(10);
    setReviewUserSearch('');
    setReviewEmailDialog(true);
    ccBccListService(1, 10, '');
  };

  const handleReviewUserSearch = (search) => {
    setReviewUserSearch(search);
    if (search === '') {
      setReviewUserPage(1);
      ccBccListService(1, reviewUserLimit, '');
    }
  };

  const handleReviewUserLimit = (event) => {
    const nextLimit = Number(event.target.value);
    setReviewUserLimit(nextLimit);
    setReviewUserPage(1);
    setReviewUserSearch('');
    ccBccListService(1, nextLimit, '');
  };

  const changeReviewUserPage = (nextPage) => {
    if (nextPage < 1 || (reviewUserTotalPages && nextPage > reviewUserTotalPages)) return;
    setReviewUserPage(nextPage);
    ccBccListService(nextPage, reviewUserLimit, reviewUserSearch);
  };

  const selectReviewUser = (user) => {
    const userCode = user?.Code || user?.UserID || user?.EmailID;
    const userEmail = user?.EmailID || user?.Email || '';
    if (!userEmail) return;

    const isCodeExists = reviewEmailRows.some(row => row.code === userCode || row.email === userEmail);
    if (isCodeExists) {
      showNotification('toast-error', 'Email already selected');
      return;
    }

    setReviewEmailRows(rows => [
      ...rows,
      {
        email: userEmail,
        type: reviewEmailType,
        code: userCode,
      },
    ]);
    setReviewEmailDialog(false);
  };

  const submitSelectedViewAction = () => {
    if (!selectedViewAction) return;
    if (selectedViewAction.status !== 'APPROVED' && !actionRemarks.trim()) {
      showNotification('toast-error', 'Remarks are required');
      return;
    }
    submitViewAction(selectedViewAction.status, actionRemarks.trim());
  };

  const reportColumns = reportDialog.action === 'COMP_REPORT'
    ? [
      ['PO Item', 'POItem'],
      ['Field Name', 'FieldName'],
      ['OCR/Physical Invoice', 'OCR/Physical Invoice'],
      ['GST Portal', 'GST Portal'],
      ['PO/GRN', 'PO/GRN'],
    ]
    : [
      ['Supplier Code', 'SupplierCode'],
      ['PO Number', 'PONumber'],
      ['Invoice Request No', 'InvoiceRequestNo'],
      ['GR Number', 'GRNumber'],
      ['GR Date', 'GRDate', 'date'],
      ['Invoice Number', 'InvoiceNumber'],
      ['Invoice Date', 'InvoiceDate', 'date'],
      ['PO Item', 'POItem'],
      ['Material Code', 'MaterialCode'],
      ['Material Description', 'MaterialDescription'],
      ['Quantity', 'Quantity'],
      ['Entry Quantity', 'EntryQuantity'],
      ['Base UOM', 'BaseUOM'],
      ['Entry UOM', 'EntryUOM'],
      ['Rate', 'Rate'],
      ['ExchangeRate', 'ExchangeRate'],
      ['TaxRate', 'TaxRate'],
      ['HSN Code', 'HSNCode'],
      ['Is Assigned', 'IsAssigned'],
      ['Is Reversal', 'IsReversal'],
      ['Reversal Date', 'ReversalDate', 'date'],
      ['Reversal Doc No', 'ReversalDocNo'],
      ['Created By', 'CreatedBy'],
      ['Created Date', 'CreatedDate', 'date'],
    ];

  const reportCellValue = (row, key, type) => {
    const cellValue = row?.[key];
    return type === 'date' ? formatDateDisplay(cellValue) : display(cellValue);
  };

  const invoiceColumns = [
    ['PO Number', 'PO_NO', undefined, 'TH_PO_NO'],
    ['PO Item', 'PO_ITEM', undefined, 'TH_PO_ITEM'],
    ['Ext Row', 'EXT_ROW', undefined, 'TH_EXT_ROW'],
    ['Material Code', 'MAT_CODE', undefined, 'TH_MATERIAL_CODE'],
    ['Material Description', 'MAT_DESC', undefined, 'TH_MATERIAL_DESCRIPTION'],
    ['Description', 'DESC', undefined, 'TH_DESCRIPTION'],
    ['Quantity', 'QTY', 'number', 'TH_QTY'],
    ['Quantity Object', 'QTY_OBJ', undefined, 'TH_QTY_OBJECT'],
    ['UOM', 'UOM', undefined, 'TH_UOM'],
    ['Rate', 'RATE', 'amount', 'TH_RATE'],
    ['Amount', 'AMT', 'amount', 'TH_AMT'],
    ['Tax Object', 'TAX_OBJ', undefined, 'TH_TAX_OBJECT'],
    ['Tax Amount (Vendor)', 'TX_AMT', 'amount', 'TH_TX_AMT'],
    ['Gross Amount', 'GR_AMT', 'amount', 'TH_GR_AMT'],
    ['HSN Code', 'HSN_CODE', undefined, 'TH_HSN_CODE'],
    ['Description Narration', 'DESC_CODE', undefined, 'TH_DESC_CODE'],
    ['Cost Center', 'CST_CNTR', undefined, 'TH_CST_CNTR'],
    ['GL Code', 'GL_CODE', undefined, 'TH_GL_CODE'],
    ['Discount Amount', 'DIS_AMT', 'amount', 'TH_DIS_AMT'],
    ['Discount Percent', 'DIS_PER', 'number', 'TH_DIS_PER'],
    ['Oth Charges', 'OTHR_CHRG', 'amount', 'TH_OTHR_CHRG'],
    ['Tax Code', 'TX_CODE', undefined, 'TH_TX_CODE'],
    ['Tax Amount (Calculated %)', 'TX_AMT_CAL_PER', 'number', 'TH_TX_AMT_CAL_PER'],
    ['Tax Amount (Calculated)', 'TX_AMT_CAL', 'amount', 'TH_TX_AMT_CAL'],
    ['Project', 'PROJECT', undefined, 'TH_PROJECT'],
    ['Asset', 'ASSET', undefined, 'TH_ASSET'],
  ];

  const adjustmentColumns = [
    ['Charge Type', 'CHARGE_TYPE'],
    ['Description', 'DESC', undefined, 'TH_DESCRIPTION'],
    ['Quantity', 'QTY', 'number', 'TH_QTY'],
    ['Quantity Object', 'QTY_OBJ', undefined, 'TH_QTY_OBJECT'],
    ['UOM', 'UOM', undefined, 'TH_UOM'],
    ['Rate', 'RATE', 'amount', 'TH_RATE'],
    ['Amount', 'AMT', 'amount', 'TH_AMT'],
    ['Tax Object', 'TAX_OBJ', undefined, 'TH_TAX_OBJECT'],
    ['Tax Amount (Vendor)', 'TX_AMT', 'amount', 'TH_TX_AMT'],
    ['Gross Amount', 'GR_AMT', 'amount', 'TH_GR_AMT'],
    ['HSN Code', 'HSN_CODE', undefined, 'TH_HSN_CODE'],
    ['Description Narration', 'DESC_CODE', undefined, 'TH_DESC_CODE'],
    ['Cost Center', 'CST_CNTR', undefined, 'TH_CST_CNTR'],
    ['GL Code', 'GL_CODE', undefined, 'TH_GL_CODE'],
    ['Discount Amount', 'DIS_AMT', 'amount', 'TH_DIS_AMT'],
    ['Discount Percent', 'DIS_PER', 'number', 'TH_DIS_PER'],
    ['Oth Charges', 'OTHR_CHRG', 'amount', 'TH_OTHR_CHRG'],
    ['Tax Code', 'TX_CODE', undefined, 'TH_TX_CODE'],
    ['Tax Amount (Calculated %)', 'TX_AMT_CAL_PER', 'number', 'TH_TX_AMT_CAL_PER'],
    ['Tax Amount (Calculated)', 'TX_AMT_CAL', 'amount', 'TH_TX_AMT_CAL'],
    ['Project', 'PROJECT', undefined, 'TH_PROJECT'],
    ['Asset', 'ASSET', undefined, 'TH_ASSET'],
    ['PO Number', 'PO_NO', undefined, 'TH_PO_NO'],
    ['PO Item', 'PO_ITEM', undefined, 'TH_PO_ITEM'],
    ['Ext Row', 'EXT_ROW', undefined, 'TH_EXT_ROW'],
    ['Material Code', 'MAT_CODE', undefined, 'TH_MATERIAL_CODE'],
    ['Material Description', 'MAT_DESC', undefined, 'TH_MATERIAL_DESCRIPTION'],
  ];

  const numberValue = data => {
    const num = parseFloat(data || 0);
    return Number.isNaN(num) ? 0 : num;
  };

  const amountValue = data => numberValue(data).toFixed(2);

  const lineRawValue = (item, key) => {
    const values = item?.values || {};
    if (values[key] !== undefined && values[key] !== null && values[key] !== '') return values[key];

    const taxAmount = numberValue(item?.CGST) + numberValue(item?.SGST) + numberValue(item?.IGST) + numberValue(item?.UGST);
    const qty = numberValue(item?.Quantity);
    const rate = numberValue(item?.Rate);
    const amount = qty * rate;
    const discountAmount = numberValue(item?.DiscountAmount);
    const otherCharges = numberValue(item?.OtherCharges);
    const grossAmount = amount + taxAmount + otherCharges - discountAmount;
    const receivedQty = numberValue(item?.RecievedQty);

    const map = {
      PO_NO: item?.PONumber,
      PO_ITEM: item?.POItem,
      EXT_ROW: item?.EXTRow,
      MAT_CODE: item?.MaterialCode,
      MAT_DESC: item?.MaterialDescription,
      DESC: item?.MaterialExtraDescription,
      QTY: qty,
      QTY_OBJ: {
        INV_QTY: item?.InvoicedQty,
        PO_QTY: item?.POQty,
        REC_QTY: receivedQty,
        SHRT_QTY: qty - receivedQty,
        SHRT_REMARKS: item?.ShortageRemarks,
      },
      UOM: dropdownLabel('INVOICEUOM', item?.MeasurementUnitID || item?.UOM || item?.UnitOfMeasure, item?.UOM || item?.UnitOfMeasure),
      RATE: rate,
      AMT: amount,
      TAX_OBJ: {
        CGST: item?.CGST,
        SGST: item?.SGST,
        IGST: item?.IGST,
        UGST: item?.UGST,
      },
      TX_AMT: taxAmount,
      GR_AMT: grossAmount,
      HSN_CODE: item?.HSNSACCode || item?.HSNCode || item?.HSN_CODE,
      DESC_CODE: item?.Description,
      CST_CNTR: item?.CostCenterName || item?.CostCenter,
      GL_CODE: item?.GLCodeName || item?.GLCode,
      DIS_AMT: discountAmount,
      DIS_PER: item?.DiscountPercent,
      OTHR_CHRG: otherCharges,
      TX_CODE: dropdownLabel('INVOICETDSTAXCODE', item?.TaxCodeID || item?.TaxCode, item?.TaxCode),
      TX_AMT_CAL_PER: item?.TaxCodeID && amount ? (taxAmount / amount) * 100 : '',
      TX_AMT_CAL: item?.TaxCodeID ? taxAmount : '',
      PROJECT: item?.ProjectCode,
      ASSET: item?.AssetCode,
      CHARGE_TYPE: dropdownLabel('CHARGETYPE', item?.ChargeTypeID || item?.ChargeType, item?.ChargeType),
    };
    return map[key];
  };

  const renderLineCell = (item, key, type) => {
    const raw = lineRawValue(item, key);
    if (key === 'QTY_OBJ' && raw && typeof raw === 'object') {
      return `Inv:${display(raw.INV_QTY, 0)} | PO:${display(raw.PO_QTY, 0)} | Rec:${display(raw.REC_QTY, 0)} | Short:${display(raw.SHRT_QTY, 0)}`;
    }
    if (key === 'TAX_OBJ' && raw && typeof raw === 'object') {
      return `CGST:${amountValue(raw.CGST)} | SGST:${amountValue(raw.SGST)} | IGST:${amountValue(raw.IGST)} | UGST:${amountValue(raw.UGST)}`;
    }
    if (raw && typeof raw === 'object') return raw.code || raw.name || raw.text || raw.id || '';
    if (type === 'amount') return amountValue(raw);
    if (type === 'number') return raw === '' || raw === undefined || raw === null ? '0' : raw;
    return display(raw);
  };

  const openLineValuePopup = (item, key) => {
    const raw = lineRawValue(item, key);
    if (!raw || typeof raw !== 'object') return;

    const rows = key === 'QTY_OBJ'
      ? [
        ['Invoiced Quantity', raw.INV_QTY],
        ['PO Quantity', raw.PO_QTY],
        ['Received Qty', raw.REC_QTY],
        ['Shortage Qty', raw.SHRT_QTY],
        ['Shortage Remarks', raw.SHRT_REMARKS],
      ]
      : [
        ['CGST', amountValue(raw.CGST)],
        ['SGST', amountValue(raw.SGST)],
        ['IGST', amountValue(raw.IGST)],
        ['UGST', amountValue(raw.UGST)],
      ];

    setLinePopup({
      visible: true,
      title: key === 'QTY_OBJ' ? 'Quantity Object' : 'Tax Object',
      rows,
    });
  };

  const renderLineCellContent = (item, key, type) => {
    if (key === 'QTY_OBJ' || key === 'TAX_OBJ') {
      return (
        <button
          type="button"
          className="invoice-view-cell-popup-btn"
          onClick={(event) => {
            event.stopPropagation();
            openLineValuePopup(item, key);
          }}
          title={key === 'QTY_OBJ' ? 'Quantity Object Details' : 'Tax Object Details'}
        >
          <i className="pi pi-search-plus" />
        </button>
      );
    }
    return renderLineCell(item, key, type);
  };

  const lineAmountTotal = (items, key) => items.reduce((sum, item) => sum + numberValue(lineRawValue(item, key)), 0);
  const subTotal = lineAmountTotal(lineItems, 'AMT');
  const taxAmount = lineAmountTotal(lineItems, 'TX_AMT');
  const lineAdjustTotal = lineAmountTotal(lineAdjust, 'AMT');
  const grandTotal = subTotal + taxAmount + lineAdjustTotal - tdsAmount;
  const getVisibleColumns = (columns, alwaysVisibleKeys = []) => (
    visibleTotalLineItems === null
      ? columns
      : columns.filter(([, , , visibleKey]) => !visibleKey || visibleTotalLineItems.includes(visibleKey) || alwaysVisibleKeys.includes(visibleKey))
  );
  const visibleInvoiceColumns = getVisibleColumns(invoiceColumns);
  const visibleAdjustmentColumns = getVisibleColumns(adjustmentColumns, ['TH_PROJECT', 'TH_ASSET', 'TH_PO_NO', 'TH_PO_ITEM', 'TH_EXT_ROW']);
  const renderGrossTotalRow = (columns, items, totalKeys, leadingCells = 2) => {
    if (!items.length) return null;
    return (
      <tr className="invoice-view-gross-total-row">
        {Array.from({ length: leadingCells }).map((_, index) => <td key={`leading-${index}`} />)}
        {columns.map(([, key]) => {
          if (key === 'RATE') return <td key={key} className="invoice-view-total-label">Gross Totals</td>;
          if (totalKeys.includes(key)) {
            return <td key={key} className={key === 'GR_AMT' ? 'invoice-view-gross-total-amount' : ''}>{amountValue(lineAmountTotal(items, key))}</td>;
          }
          return <td key={key} />;
        })}
      </tr>
    );
  };

  const readRowValue = (row, keys) => {
    const keyList = Array.isArray(keys) ? keys : [keys];
    return keyList.map(key => row?.[key]).find(item => item !== undefined && item !== null && item !== '') ?? '';
  };

  const ViewModeTable = ({ title, columns, rows, emptyText = 'No available options' }) => (
    <div className="invoice-view-tab-table-section">
      {title && <h6 className="invoice-view-tab-table-title">{title}</h6>}
      <div className="invoice-view-tab-table-wrap">
        <table className="invoice-view-tab-table">
          <thead>
            <tr>
              {columns.map(([label]) => <th key={label}>{label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows?.length > 0 ? rows.map((row, rowIndex) => (
              <tr key={row?.ID || row?.CriteriaID || row?.RowNumber || rowIndex}>
                {columns.map(([label, keys, formatter]) => {
                  const rawValue = keys === 'rowIndex' ? rowIndex + 1 : readRowValue(row, keys);
                  return <td key={label}>{display(formatter ? formatter(rawValue, row, rowIndex) : rawValue)}</td>;
                })}
              </tr>
            )) : (
              <tr>
                <td colSpan={columns.length} className="invoice-view-tab-table-empty">{emptyText}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const ViewModeTableSection = ({ children }) => (
    <div className="matCon-card invoice-view-tab-table-card">
      <div className="invoice-detail-section">{children}</div>
    </div>
  );

  const headerFields = [
    ['Request Number', value('RequestNumber'), 'RequestNumber'],
    ['Current Status', value('DisplayStatus', 'Status'), 'Status'],
    ['Invoice Type', value('InvoiceType'), 'InvoiceType'],
    ['Creation Date', formatDateDisplay(value('CreationDate')), 'CreationDate'],
  ];
  const basicFields = [
    ['Vendor Code', value('SupplierCode', 'VendorCode', 'SupplierNumber'), 'Vendor.code', value('SupplierCode', 'VendorCode', 'SupplierNumber')],
    ['Vendor Name', value('VendorName', 'SupplierName'), 'Vendor.name', value('VendorName', 'SupplierName')],
    ['Invoice Date', formatDateOnlyDisplay(value('InvoiceDate')), 'InvoiceDate', value('InvoiceDate')],
    ['Invoice Number', value('InvoiceNumber'), 'InvoiceNumber', value('InvoiceNumber')],
    ['Reference Number', value('ReferenceNumber', 'ReferenceRemarks'), 'ReferenceNumber', value('ReferenceNumber', 'ReferenceRemarks')],
    ['PO Number', value('PONumber', 'PO_NO'), 'PONumber', value('PONumber', 'PO_NO')],
    ['PO Delivery Date', formatDateDisplay(value('PODeliveryDate')), 'PODeliveryDate', value('PODeliveryDate')],
    ['PO Reference Number', value('POReferenceNumber'), 'POReferenceNumber', value('POReferenceNumber')],
    ['Contract Number', value('ContractNumber'), 'ContractNumber', value('ContractNumber')],
    ['Agreement Number', value('AgreementNumber'), 'AgreementNumber', value('AgreementNumber')],
    ['Bill To', dropdownLabel('INVOICEBILLTO', value('BillToID', 'BillTo'), value('BillToName')), 'BillToID', value('BillToID', 'BillTo', 'BillToName')],
    ['Ship To', dropdownLabel('INVOICESHIPTO', value('ShipToID', 'ShipTo'), value('ShipToName')), 'ShipToID', value('ShipToID', 'ShipTo', 'ShipToName')],
    ['Destination Of Supply', value('DestinationSupply'), 'DestinationSupply', value('DestinationSupply')],
    ['Bill To GSTIN', value('BillToGSTIN'), 'BillToGSTIN', value('BillToGSTIN')],
    ['Currency', dropdownLabel('INVOICECURRENCY', value('CurrencyID', 'Currency'), value('CurrencySymbol', 'CurrencyName')), 'CurrencyID', value('CurrencyID', 'Currency', 'CurrencySymbol', 'CurrencyName')],
    ['Invoice Priority', dropdownLabel('INVOICEPRIORITY', value('InvoicePriorityID'), value('InvoicePriority')), 'InvoicePriorityID', value('InvoicePriorityID', 'InvoicePriority')],
    ['Invoice Criticality', dropdownLabel('INVOICECRITICALITY', value('InvoiceCriticalityID'), value('InvoiceCriticality')), 'InvoiceCriticalityID', value('InvoiceCriticalityID', 'InvoiceCriticality')],
    ['Exception Remarks', value('ExceptionRemarks'), 'ExceptionRemarks', value('ExceptionRemarks')],
    ['Invoice Sub-Type', dropdownLabel('INVOICESUBTYPE', value('InvoiceSubTypeID'), value('InvoiceSubType')), 'InvoiceSubTypeID', value('InvoiceSubTypeID', 'InvoiceSubType')],
    ['Challan No', value('ChallanNo'), 'ChallanNo', value('ChallanNo')],
    ['Challan Date', formatDateDisplay(value('ChallanDate')), 'ChallanDate', value('ChallanDate')],
    ['Material Category', dropdownLabel('INVOICEMATERIALCATEGORY', value('MaterialCategoryID'), value('MaterialCategory')), 'MaterialCategoryID', value('MaterialCategoryID', 'MaterialCategory')],
  ];
  const paymentFields = [
    ['Net Payable', formatCurrency(value('NetPayable')), 'NetPayable', value('NetPayable')],
    ['Payment Terms', dropdownLabel('PAYMENTTERMS', value('PaymentTermsID', 'PaymentTerms'), value('PaymentTermsName')), 'PaymentTerms', value('PaymentTermsID', 'PaymentTerms', 'PaymentTermsName')],
    ['Billing From Date', formatDateDisplay(value('BillingFrom')), 'BillingFrom', value('BillingFrom')],
    ['Billing To Date', formatDateDisplay(value('BillingTo')), 'BillingTo', value('BillingTo')],
    ['Bank Account', dropdownLabel('INVOICEBANKACCOUNT', value('BankID', 'BankCode'), value('BankName')), 'Bank.code', value('BankID', 'BankCode', 'BankName')],
    ['Account Number', value('BankAccountNo', 'AccountNo'), 'AccountNo', value('BankAccountNo', 'AccountNo')],
    ['IFSC', value('IFSC', 'IFSCCode'), 'IFSC', value('IFSC', 'IFSCCode')],
  ];
  const gstFields = [
    ['Supplier GSTIN', dropdownLabel('INVOICEGSTIN', value('SupplierGSTIN'), value('SupplierGSTINNo', 'SupplierGSTINNumber')), 'SupplierGSTIN', value('SupplierGSTIN', 'SupplierGSTINNo', 'SupplierGSTINNumber')],
    ['Source Of Supply', value('SourceOfSupply'), 'SourceOfSupply', value('SourceOfSupply')],
    ['Original Invoice Reference', value('OriginalInvoiceReference'), 'OriginalInvoiceReference', value('OriginalInvoiceReference')],
    ['IRN Code', value('IRNCode'), 'IRNCode', value('IRNCode')],
    ['E-Invoice', value('EInvoice') === 1 || value('EInvoice') === '1' ? 'Yes' : 'No', 'EInvoice', value('EInvoice')],
  ];
  const eWayBillFields = [
    ['E Way Bill Number', value('EWayBillNumber'), 'EWayBillNumber'],
    ['E Way Bill Date', formatDateDisplay(value('EWayBillDate')), 'EWayBillDate'],
    ['Vehicle Type', dropdownLabel('VEHICLETYPE', value('VehicleTypeID', 'VehicleType'), value('VehicleTypeName')), 'VehicleType'],
    ['Vehicle Number', value('VehicleNumber'), 'VehicleNumber'],
    ['Transporter Name', value('TransporterName'), 'TransporterName'],
    ['Transporter GSTIN', value('TransporterGSTIN'), 'TransporterGSTIN'],
    ['GR/LR Number', value('GRLRNumber'), 'GRLRNumber'],
    ['GR/LR Date', formatDateDisplay(value('GRLRDate')), 'GRLRDate'],
    ['License Number', value('LicenseNumber'), 'LicenseNumber'],
    ['Driver Contact Number', value('DriverContact'), 'DriverContact'],
  ];
  const othersFields = [
    ['Text 1', value('Text1'), 'Text1'],
    ['Text 2', value('Text2'), 'Text2'],
    ['Text 3', value('Text3'), 'Text3'],
    ['Text 4', value('Text4'), 'Text4'],
    ['Date 1', formatDateDisplay(value('Date1')), 'Date1'],
    ['Date 2', formatDateDisplay(value('Date2')), 'Date2'],
    ['Date 3', formatDateDisplay(value('Date3')), 'Date3'],
    ['Numeric 1', value('Numeric1'), 'Numeric1'],
    ['Numeric 2', value('Numeric2'), 'Numeric2'],
    ['Numeric 3', value('Numeric3'), 'Numeric3'],
  ];
  const officeFields = [
    ['Department', dropdownLabel('INVOICEDEPARTMENT', value('DepartmentID'), value('DepartmentName', 'Department')), 'DepartmentID'],
    ['Cost Center', value('CostCenterCode', 'CostCenter'), 'CostCenter.code'],
    ['GL Code', value('GLCode'), 'GLCode.code'],
    ['Accounting Date', formatDateDisplay(value('AccountingDate')), 'AccountingDate'],
    ['Advance Adjustment', value('AdvanceAdjustment'), 'AdvanceAdjustment'],
    ['Other Retention', value('OtherRetention'), 'OtherRetention'],
    ['Retention Remarks', value('RetentionRemarks'), 'RetentionRemarks'],
    ['Company Code', dropdownLabel('INVOICECOMPANY', value('CompanyID', 'CompanyCode'), value('CompanyCode')), 'CompanyCode'],
    ['Plant Code', dropdownLabel('INVOICEPLANT', value('PlantID', 'PlantCode'), value('PlantCode')), 'PlantCode'],
  ];
  const generalPoLineRows = poLineRows.length > 0 ? poLineRows : lineItems.map((item, index) => ({
    RowNumber: index + 1,
    MaterialCode: lineRawValue(item, 'MAT_CODE'),
    PoItem: lineRawValue(item, 'PO_ITEM'),
    ExtRow: lineRawValue(item, 'EXT_ROW'),
    PoQty: lineRawValue(item, 'QTY_OBJ')?.PO_QTY,
    Quantity: lineRawValue(item, 'QTY_OBJ')?.INV_QTY || lineRawValue(item, 'QTY'),
  }));
  const generalInvoiceRows = invoiceWithPoRows.length > 0 ? invoiceWithPoRows : (value('PONumber') ? [{
    ReferenceNumber: value('ReferenceNumber', 'RequestNumber'),
    VendorName: value('VendorName', 'SupplierName'),
    PONumber: value('PONumber'),
    NetPayable: value('NetPayable'),
    InvoiceNumber: value('InvoiceNumber'),
  }] : []);
  const vendorChecklistTable = (
    <ViewModeTableSection>
      <ViewModeTable
        title="Download Attachments"
        columns={[
          ['#', 'RowNumber', (data, row, index) => data || index + 1],
          ['Check List', ['CheckListName', 'VendorCheckList', 'CheckListStatus']],
          ['Options', ['CriteriaValue', 'OptionName', 'Options']],
          ['Remarks', ['Remarks', 'VendorCheckListRemarks', 'CheckListRemarks']],
          ['', ['AttachmentName', 'DocumentName']],
        ]}
        rows={checkListRows}
      />
    </ViewModeTableSection>
  );
  const vendorPerformanceTable = (
    <ViewModeTableSection>
      <ViewModeTable
        columns={[
          ['#', 'RowNumber', (data, row, index) => data || index + 1],
          ['Evaluation Criteria', ['RatingName', 'EvaluationCriteria', 'VendorPerformanceRating', 'PerformanceRating']],
          ['Rating', ['CriteriaRatingValue', 'Rating', 'RatingValue']],
          ['Remarks', ['Remarks', 'VendorPerformanceRemarks', 'PerformanceRemarks']],
        ]}
        rows={vendorPerformanceRows}
      />
    </ViewModeTableSection>
  );
  const generalInfoTables = (
    <ViewModeTableSection>
      <ViewModeTable
        title="Invoices with this PO Number"
        columns={[
          ['Material Code', ['MaterialCode', 'MAT_CODE']],
          ['PO Item', ['PoItem', 'POItem', 'PO_ITEM']],
          ['Extrow', ['ExtRow', 'EXTRow', 'EXT_ROW']],
          ['PO Quantity', ['PoQty', 'POQty']],
          ['Invoiced Quantity', ['Quantity', 'InvoicedQty', 'QTY']],
        ]}
        rows={generalPoLineRows}
        emptyText="No data available"
      />
      <ViewModeTable
        title="PO LineItems"
        columns={[
          ['Reference Number', ['ReferenceNumber', 'RequestNumber']],
          ['Vendor Name', ['VendorName', 'SupplierName']],
          ['PO Number', ['PONumber', 'PO_NO']],
          ['Amount', ['NetPayable', 'Amount', 'AMT'], data => (data === '' ? '' : amountValue(data))],
          ['Invoice Number', ['InvoiceNumber']],
        ]}
        rows={generalInvoiceRows}
        emptyText="No data available"
      />
    </ViewModeTableSection>
  );
  const superAdminFields = [
    ['Alternate Approval', dropdownLabel('INVOICEALTERNATEAPPROVAL', value('AlternateApproverID', 'AltApproval'), value('AltApproval')), 'AltApproval'],
    ['Receipt Date', formatDateDisplay(value('ReceiptDate')), 'ReceiptDate'],
    ['Next Approver', value('NextApprover'), 'NextApprover'],
    ['Actual Status', value('ActualStatus'), 'ActualStatus'],
    ['Reset Approval Status', value('ResetApprovalStatus'), 'ResetApprovalStatus'],
  ];
  const tabPanels = [
    ['Basic Details', 'divBD', 'BasicDetails', <SectionFields className="vendor-invoice-detail-section">{renderFields(basicFields)}</SectionFields>],
    ['Payment Details', 'divPD', 'PaymentDetails', <SectionFields>{renderFields(paymentFields)}</SectionFields>],
    ['GST Details', 'divGD', 'GstDetails', <SectionFields>{renderFields(gstFields)}</SectionFields>],
    ['E Way Bill', 'divEWB', 'EWayBill', <SectionFields>{renderFields(eWayBillFields)}</SectionFields>],
    ['Others', 'divOT', 'Others', <SectionFields>{renderFields(othersFields)}</SectionFields>],
    ['Office Use', 'divOU', 'OfficeUse', <SectionFields>{renderFields(officeFields)}</SectionFields>],
    ['Vendor Check List', 'divVCL', 'VendorCheckList', vendorChecklistTable],
    ['Vendor Performance Rating', 'divVPR', 'VendorPerformanceRating', vendorPerformanceTable],
    ['General Info', 'divGI', 'GeneralInfo', generalInfoTables],
    ['Super Admin Rights', 'divSAR', 'SuperAdminRights', <SectionFields>{renderFields(superAdminFields)}</SectionFields>],
  ].filter(([, , visibleKey]) => isVisible(visibleKey));

  const selectedActionTone = selectedViewAction?.tone || 'approve';
  const selectedActionLabel = selectedViewAction?.label || 'Take Action';
  const selectedActionIsReview = selectedViewAction?.status === 'REVIEWED';

  if (loading) return <InvoiceLoader />;

  if (!invoiceData) {
    return (
      <div>
        <Navbar />
        <div className="container mt-5">
          <div className="alert alert-warning">Invoice not found</div>
          <button className="btn btn-primary" onClick={() => navigate(-1)}>Go Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="invoice-view-page">
      <Navbar />
      <div className="page-container inv-container invoice-view-container">
        <div className="head-section invoice-view-titlebar">
          <div className="info-container">
            <div className="info-item">
              <h6 className="value-label">
                Invoice Generation
                <span className="invoice-view-mode-badge">{display(value('DisplayStatus', 'Status'), 'View Mode')}</span>
              </h6>
            </div>
          </div>
          <div className="invoice-view-actions">
            {(value('Status')?.toUpperCase() === 'FINAL' || value('Status')?.toUpperCase() === 'DRAFT') && (
              <button type="button" className="head-btn invoice-toolbar-outline invoice-toolbar-print" onClick={getVendorInvoicePrintData}>
                <i className="pi pi-print" />
                Print
              </button>
            )}
            {canShowPushToSap && (
              <button type="button" className="head-btn invoice-toolbar-outline invoice-toolbar-sap" onClick={pushToSapDataShow}>
                <i className="pi pi-upload" />
                Push To SAP
              </button>
            )}
            {ocrDocumentID && (
              <>
                <span className="invoice-toolbar-divider" />
                  <button
                    type="button"
                    className="head-btn icon invoice-toolbar-icon invoice-toolbar-log"
                    title="Comparison Report"
                    onClick={() => openReportDialog('COMP_REPORT')}
                  >
                  <TbColumns size={18} />
                </button>
                {value('POType')?.toUpperCase() !== 'VCPO' && (
                  <button
                    type="button"
                    className="head-btn icon invoice-toolbar-icon invoice-toolbar-hierarchy"
                    title="GRN Report"
                    onClick={() => openReportDialog('GRN_REPORT')}
                  >
                    <FaTruck size={18} />
                  </button>
                )}
                <button
                  type="button"
                  className="head-btn icon invoice-toolbar-icon invoice-toolbar-ocr"
                  title="OCR Data"
                  onClick={() => setJsonDialog(true)}
                >
                  <TbLayoutGridAdd size={18} />
                </button>
                <span className="invoice-toolbar-divider" />
              </>
            )}
            <div className="invoice-action-menu-wrap">
              <button type="button" className="head-btn invoice-toolbar-action" onClick={() => setTakeActionMenuOpen(prev => !prev)}>
                Take Action
                <i className={`pi ${takeActionMenuOpen ? 'pi-chevron-up' : 'pi-chevron-down'}`} />
              </button>
              {takeActionMenuOpen && (
                <div className="invoice-action-menu">
                  {actionOptions.map((action, index) => (
                    <button
                      type="button"
                      key={action.status}
                      className={`invoice-action-menu-item ${action.tone} ${index === 3 ? 'review-separator' : ''}`}
                      onClick={() => openViewAction(action)}
                    >
                      <i className={`pi ${action.icon}`} />
                      <span>{action.menuLabel}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button type="button" className="head-btn invoice-toolbar-history" onClick={fetchApprovalHistory}>
              <i className="pi pi-history" />
              Approval History
            </button>
            <button type="button" className="head-btn invoice-toolbar-back" onClick={() => navigate('/Vendor/Invoice/List', { replace: true })}>
              <i className="pi pi-arrow-left" />
              Back To List
            </button>
          </div>
        </div>

        <div className={showPreview ? 'invoice-view-layout has-preview' : 'invoice-view-layout no-preview'}>
          {showPreview && (
            <aside className="invoice-view-preview">
              {previewUrl && (
                <div className="invoice-view-preview-actions" aria-label="Document actions">
                  <button type="button" title="Download" onClick={downloadPreviewDocument}>
                    <FiDownload />
                  </button>
                  <button type="button" title="Print" onClick={printPreviewDocument}>
                    <FaPrint />
                  </button>
                </div>
              )}
              <div className="invoice-view-preview-toolbar">
                <button type="button" className="invoice-preview-close-btn" onClick={() => setShowPreview(false)}>
                  <i className="pi pi-angle-double-left" />
                  Close Preview
                </button>
                <div className="invoice-preview-file">
                  <i className="pi pi-file" />
                  <span>{value('InvoiceDocumentName', 'OCRDocumentName') || 'invoice.pdf'}</span>
                  <b>/ 1</b>
                </div>
                <div className="invoice-preview-tools">
                  <i className="pi pi-search" />
                  <i className="pi pi-download" />
                  <i className="pi pi-print" />
                </div>
              </div>
              <div className="invoice-view-preview-body">
                {previewUrl ? (
                  isPdf ? <iframe title="Invoice Preview" src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH&zoom=page-width`} /> : <img src={previewUrl} alt="Invoice Preview" />
                ) : (
                  <div className="invoice-view-preview-empty">No document preview available</div>
                )}
              </div>
            </aside>
          )}

          <main className="invoice-view-main">
            <button type="button" className="invoice-preview-toggle" onClick={() => setShowPreview(prev => !prev)}>
              <i className={`pi ${showPreview ? 'pi-angle-double-left' : 'pi-angle-double-right'}`} />
              {showPreview ? 'Close preview' : 'Open preview'}
            </button>

            <div className="section-matCon-list invoice-view-header-summary">
              <SectionFields>
                {headerFields.map(([label, fieldValue, fieldKey]) => (
                  <Field key={label} label={label} className="col-md-3" fieldKey={fieldKey}>{fieldValue}</Field>
                ))}
              </SectionFields>
            </div>

            <div className="tab-section inv-tabview invoice-view-tabs">
              <TabView className="material-tab" activeIndex={Math.min(activeIndex, Math.max(tabPanels.length - 1, 0))} onTabChange={e => setActiveIndex(e.index)} renderActiveOnly={false}>
                {tabPanels.map(([header, tabId, , content]) => (
                  <TabPanel key={tabId} header={header} className="custom-tab" id={tabId}>{content}</TabPanel>
                ))}
              </TabView>
            </div>

            <section className="section-matCon-list inv-table invoice-view-section">
              <div className="matCon-card">
                <div className="detail-add"><h6 className="detail-heading">Invoice Details</h6></div>
                <div className="invoice-screen-table-wrap">
                  <table className="invoice-screen-table">
                    <thead>
                      <tr>
                        <th className="invoice-col-sno">S.No</th>
                        <th className="invoice-col-action">Action</th>
                        {visibleInvoiceColumns.map(([label, key]) => (
                          <th key={key}>{label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.length > 0 ? lineItems.map((item, index) => (
                        <tr key={lineValue(item, 'InvoiceLineItemID', 'LineItemID') || index}>
                          <td>{index + 1}</td>
                          <td className="invoice-view-action-cell">
                            <button type="button" className="invoice-view-line-btn" title="View Line Item" onClick={() => openLineDrawer(index, 'invoice')}>
                              <FaEye />
                            </button>
                          </td>
                          {visibleInvoiceColumns.map(([, key, type]) => (
                            <td key={key} className={type ? 'screen-qty-cell' : ''}>
                              <div className={`screen-readonly-cell ${key === 'PO_NO' || key === 'GR_AMT' ? 'link-cell' : ''} ${key === 'DESC' || key === 'MAT_DESC' ? 'text-cell' : ''}`}>
                                {renderLineCellContent(item, key, type)}
                              </div>
                            </td>
                          ))}
                        </tr>
                      )) : (
                        <tr><td colSpan={visibleInvoiceColumns.length + 2} className="empty-table">No data available</td></tr>
                      )}
                      {renderGrossTotalRow(visibleInvoiceColumns, lineItems, ['DIS_AMT', 'OTHR_CHRG', 'AMT', 'TX_AMT', 'TX_AMT_CAL', 'GR_AMT'])}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            <section className="section-matCon-list inv-table invoice-view-section">
              <div className="matCon-card">
                <div className="detail-add"><h6 className="detail-heading">Lineitems Adjustments</h6></div>
                <div className="invoice-screen-table-wrap">
                  <table className="invoice-screen-table invoice-adjustment-table">
                    <thead>
                      <tr>
                        <th className="invoice-col-sno">S.No</th>
                        <th className="invoice-col-action">Action</th>
                        {visibleAdjustmentColumns.map(([label, key]) => (
                          <th key={key}>{label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {lineAdjust.length > 0 ? lineAdjust.map((item, index) => (
                        <tr key={lineValue(item, 'InvoiceLineItemAmountID', 'LineItemAmountID') || index}>
                          <td>{index + 1}</td>
                          <td className="invoice-view-action-cell">
                            <button type="button" className="invoice-view-line-btn" title="View Line Item" onClick={() => openLineDrawer(index, 'adjustment')}>
                              <FaEye />
                            </button>
                          </td>
                          {visibleAdjustmentColumns.map(([, key, type]) => (
                            <td key={key} className={type ? 'screen-qty-cell' : ''}>
                              <div className={`screen-readonly-cell ${key === 'PO_NO' || key === 'GR_AMT' ? 'link-cell' : ''} ${key === 'DESC' || key === 'MAT_DESC' ? 'text-cell' : ''}`}>
                                {renderLineCellContent(item, key, type)}
                              </div>
                            </td>
                          ))}
                        </tr>
                      )) : (
                        <tr><td colSpan={visibleAdjustmentColumns.length + 2} className="empty-table">No data available</td></tr>
                      )}
                      {renderGrossTotalRow(visibleAdjustmentColumns, lineAdjust, ['DIS_AMT', 'OTHR_CHRG', 'AMT', 'TX_AMT', 'TX_AMT_CAL', 'GR_AMT'])}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            <section className="invoice-view-footer-row">
              <div className="section-matCon-list invoice-footer-form">
                <div className="matCon-card">
                <div className="detail-add"><h6 className="detail-heading">Invoice Footer</h6></div>

                  <div className="invoice-detail-section">
                    <div className="row mt-0">
                      <Field label="TCS" className="col-md-4" fieldKey="TCS" numeric>{formatCurrency(value('TCS', 0))}</Field>
                      <Field label="Discount" className="col-md-4" fieldKey="Discount" numeric>{formatCurrency(value('Discount', 0))}</Field>
                      <Field label="TDS Tax Code" className="col-md-4" fieldKey="TDSCodeID">
                        {dropdownLabel('INVOICETDSTAXCODE', value('TDSCodeID', 'POTDSMappingID'), value('TDSCode'))}
                      </Field>
                      <Field label="TDS Percent" className="col-md-4" fieldKey="TDSPercent" numeric>{value('TDSPercent') || '0.00'}</Field>
                      <Field label="TDS Amount" className="col-md-4" fieldKey="TDSAmount" numeric>{formatCurrency(value('TDSAmount', 0))}</Field>
                    </div>
                  </div>
                </div>
              </div>
              <div className="matCon-card invoice-summary-screen">
                <div><span>Sub Total</span><b>{formatCurrency(subTotal)}</b></div>
                <div><span>Tax Amount</span><b>{formatCurrency(taxAmount)}</b></div>
                <div><span>Adjustments</span><b>{formatCurrency(lineAdjustTotal)}</b></div>
                <div className="grand"><span>Grand Total</span><b>{formatCurrency(grandTotal)}</b></div>
              </div>
            </section>

            <section className="section-matCon-list invoice-view-section invoice-view-document-section invoice-view-attachments">
              <div className="matCon-card invoice-view-attachment-card">
                <div className="detail-add mt-15 invoice-view-attachment-head">
                  <h6 className="detail-heading"><i className="pi pi-paperclip" /> Attachment Details</h6>
                </div>
                <AttachmentTable
                  reqId={id ?? ''}
                  action="VIEW"
                  rowData={{ headerID: id, RequestNumber: value('RequestNumber') }}
                  onDocsRowsChange={() => {}}
                  moduleCode="InvoiceGen"
                  formCode="Invoice"
                  dropdownAction="FETCH_DOCUMENT_CONFIG_DATA"
                  getAttachAction="GET_UPLOADED_DOCUMENT_LIST_BY_FORM_MODULE"
                  confirm
                />
                <div id="divOfficialAttachment">
                  <div id="OfficialAttachment">
                    <div className="detail-add mt-15 invoice-view-attachment-head">
                      <h6 className="detail-heading"><i className="pi pi-cog" /> Official Attachment Details</h6>
                    </div>
                    <AttachmentTable
                      reqId={id ?? ''}
                      action="VIEW"
                      rowData={{ headerID: id, RequestNumber: value('RequestNumber') }}
                      onDocsRowsChange={() => {}}
                      moduleCode="InvoiceOfficeAttachment"
                      formCode="Invoice"
                      dropdownAction="FETCH_DOCUMENT_CONFIG_DATA"
                      getAttachAction="GET_UPLOADED_DOCUMENT_LIST_BY_FORM_MODULE"
                      confirm
                    />
                  </div>
                </div>
              </div>
            </section>

            {value('TransType')?.toUpperCase() === 'POBASE' && value('PONumber') !== '' && (
              <section className="section-matCon-list invoice-view-section invoice-view-document-section">
                <div className="matCon-card invoice-view-document-card">
                  <div className="detail-add mt-15">
                    <h6 className="detail-heading">PO SAP Document</h6>
                  </div>
                  <AttachmentTable
                    reqId={value('PONumber')}
                    action="VIEW"
                    rowData={{ headerID: id, RequestNumber: value('RequestNumber') }}
                    onDocsRowsChange={() => {}}
                    moduleCode="SAP"
                    formCode="PurchaseOrder"
                    getAttachAction="GET_SAP_DOCUMENTS"
                    confirm
                  />
                </div>
              </section>
            )}
          </main>
        </div>

      </div>

      {selectedLineItem && (
        <div className="invoice-line-drawer-overlay">
          <div className="invoice-line-drawer-backdrop" onClick={closeLineDrawer} />
          <aside className="invoice-line-drawer">
            <div className="invoice-line-drawer-head">
              <div>
                <h5>Line Item Details</h5>
                {/* <span>Granular view - Item ID: {lineValue(selectedLineItem, 'InvoiceLineItemID', 'LineItemID') || selectedLineIndex + 1}</span> */}
              </div>
              <div className="invoice-line-drawer-head-actions">
                <button type="button" disabled={selectedLineIndex === 0} onClick={() => setSelectedLineIndex(index => Math.max(0, index - 1))}>
                  <i className="pi pi-angle-left" />
                  Previous
                </button>
                <button type="button" disabled={selectedLineIndex === selectedLineList.length - 1} onClick={() => setSelectedLineIndex(index => Math.min(selectedLineList.length - 1, index + 1))}>
                  Next
                  <i className="pi pi-angle-right" />
                </button>
                <button type="button" className="invoice-line-drawer-close" onClick={closeLineDrawer}><FiX /></button>
              </div>
            </div>
            <div className="invoice-line-drawer-grid">
              <div><label>Line Number</label><span>{selectedLineIndex + 1}</span></div>
              <div><label>Material ID</label><span>{renderLineCell(selectedLineItem, 'MAT_CODE')}</span></div>
              <div><label>PO Number</label><span>{renderLineCell(selectedLineItem, 'PO_NO')}</span></div>
              <div><label>PO Item</label><span>{renderLineCell(selectedLineItem, 'PO_ITEM')}</span></div>
              <div><label>Ext Row</label><span>{renderLineCell(selectedLineItem, 'EXT_ROW')}</span></div>
              <div className="wide"><label>Description</label><span>{renderLineCell(selectedLineItem, 'MAT_DESC')}</span></div>
              <div><label>Quantity</label><span>{renderLineCell(selectedLineItem, 'QTY', 'number')}</span></div>
              <div><label>Unit Of Measure</label><span>{renderLineCell(selectedLineItem, 'UOM')}</span></div>
              <div><label>Unit Rate</label><span>{formatCurrency(lineRawValue(selectedLineItem, 'RATE'))}</span></div>
              <div><label>Taxable Amount</label><span>{formatCurrency(lineRawValue(selectedLineItem, 'AMT'))}</span></div>
              <div><label>Tax Component</label><span>{formatCurrency(lineRawValue(selectedLineItem, 'TX_AMT'))}</span></div>
              <div><label>Total Gross</label><span>{formatCurrency(lineRawValue(selectedLineItem, 'GR_AMT'))}</span></div>
              <div><label>HSN Mapping</label><span>{renderLineCell(selectedLineItem, 'HSN_CODE')}</span></div>
            </div>
            <div className="invoice-line-drawer-footer">
            </div>
            <button type="button" className="invoice-line-dismiss" onClick={closeLineDrawer}>Dismiss</button>
          </aside>
        </div>
      )}

      <Dialog
        visible={takeActionVisible}
        draggable={false}
        className="invoice-action-dialog"
        style={{ width: selectedActionIsReview ? '620px' : '420px' }}
        position="top"
        onHide={() => {
          setTakeActionVisible(false);
          setSelectedViewAction(null);
          setActionRemarks('');
        }}
        showHeader={false}
        blockScroll
      >
        <div className={`invoice-action-modal ${selectedActionIsReview ? 'review-mode' : 'confirm-mode'} ${selectedActionTone}-mode`}>
          {selectedActionIsReview ? (
            <>
              <div className="invoice-review-modal-head">
                <h3>Vendor Invoice Review - {value('RequestNumber') || ''}</h3>
                <button type="button" onClick={() => setTakeActionVisible(false)}><i className="pi pi-times" /></button>
              </div>
              <div className="invoice-review-section-head">
                <h4>Review Configuration</h4>
              </div>
              <div className="invoice-review-radio-row">
                <label className="invoice-review-option selected">
                  <input type="radio" checked readOnly />
                  <strong>Creator</strong>
                  <span>Assign back to initiator</span>
                </label>
                <div>
                  <label className="invoice-review-option">
                    <input
                      type="radio"
                      checked={reviewResetOption === 1}
                      onChange={() => setReviewResetOption(1)}
                    />
                    <strong>Reset & Review</strong>
                    <span>Restart approval chain</span>
                  </label>
                  <label className="invoice-review-option">
                    <input
                      type="radio"
                      checked={reviewResetOption === 0}
                      onChange={() => setReviewResetOption(0)}
                    />
                    <strong>Only Review</strong>
                    <span>Standard audit trail</span>
                  </label>
                </div>
              </div>
              <div className="invoice-review-section-head invoice-review-remarks-head">
                <h4>Review Remarks</h4>
                <span>Optional</span>
              </div>
              <textarea
                className="invoice-review-remarks"
                value={actionRemarks}
                onChange={event => setActionRemarks(event.target.value)}
                placeholder="Enter detailed audit notes or reason for review..."
              />
              <div className="invoice-review-recipient-head">
                <div>
                  <h4>Recipient Management</h4>
                  <span>Manage additional stakeholders for this review cycle.</span>
                </div>
                <div className="invoice-review-email-actions">
                  <button type="button" onClick={() => openReviewEmailDialog('CC')}><i className="pi pi-plus" /> CC</button>
                  <button type="button" onClick={() => openReviewEmailDialog('BCC')}><i className="pi pi-plus" /> BCC</button>
                </div>
              </div>
              <div className="invoice-review-email-table-wrap">
                <table className="invoice-review-email-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Recipient Email</th>
                      <th>CC</th>
                      <th>BCC</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reviewEmailRows.length > 0 ? reviewEmailRows.map((row, index) => (
                      <tr key={`${row.email}-${index}`}>
                        <td>{index + 1}</td>
                        <td>{row.email}</td>
                        <td>{row.type === 'CC' ? <i className="pi pi-check review-check" /> : '-'}</td>
                        <td>{row.type === 'BCC' ? <i className="pi pi-check review-check" /> : '-'}</td>
                        <td>
                          <button type="button" className="review-delete" onClick={() => setReviewEmailRows(rows => rows.filter((_, rowIndex) => rowIndex !== index))}>
                            <i className="pi pi-trash" />
                          </button>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="5" className="invoice-review-empty">
                          <div className="invoice-review-empty-state">
                            <i className="pi pi-envelope" />
                            <strong>No recipients added</strong>
                            <span>Use the buttons above to add stakeholders to this review.</span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <>
              <div className="invoice-confirm-action-head">
                <span className="invoice-confirm-action-icon"><i className={`pi ${selectedViewAction?.icon || 'pi-info-circle'}`} /></span>
                <h3>Are you sure?</h3>
              </div>
              <div className="invoice-confirm-action-message">
                <strong>{selectedActionLabel} Request</strong>
                <span>{selectedViewAction?.help}</span>
              </div>
              <label>{selectedViewAction?.remarksLabel || 'Remarks'}</label>
              <textarea
                className="invoice-confirm-remarks"
                value={actionRemarks}
                onChange={event => setActionRemarks(event.target.value)}
                placeholder={selectedViewAction?.placeholder || 'Enter remarks...'}
              />
            </>
          )}
          <div className="invoice-action-modal-footer">
            {selectedActionIsReview && (
              <button
                type="button"
                className="invoice-action-cancel-link"
                onClick={() => {
                  setTakeActionVisible(false);
                  setSelectedViewAction(null);
                  setActionRemarks('');
                }}
                disabled={takeActionLoading}
              >
                CANCEL
              </button>
            )}
            <button
              type="button"
              className="invoice-action-cancel"
              onClick={() => {
                setTakeActionVisible(false);
                setSelectedViewAction(null);
                setActionRemarks('');
              }}
              disabled={takeActionLoading}
            >
              {selectedActionIsReview ? 'Back' : 'Cancel'}
            </button>
            <button
              type="button"
              className={`invoice-action-submit ${selectedActionTone}`}
              onClick={submitSelectedViewAction}
              disabled={takeActionLoading}
            >
              {selectedActionIsReview ? 'Submit Review' : selectedActionLabel}
            </button>
          </div>
        </div>
      </Dialog>

      <Dialog
        header={`${reviewEmailType} Users`}
        visible={reviewEmailDialog}
        draggable={false}
        className="invoice-review-user-dialog"
        style={{ width: '760px' }}
        position="top"
        onHide={() => setReviewEmailDialog(false)}
        blockScroll
      >
        <div className="invoice-review-user-toolbar">
          <div className="invoice-report-page-size">
            <span>Show</span>
            <select value={reviewUserLimit} onChange={handleReviewUserLimit}>
              {[10, 25, 50, 75, 100, 200, 500, 1000].map(item => <option key={item} value={item}>{item}</option>)}
            </select>
            <span>entries</span>
          </div>
          <div className="invoice-report-search">
            <input
              type="search"
              className="table-search-data"
              value={reviewUserSearch}
              onChange={event => handleReviewUserSearch(event.target.value)}
              placeholder="Search users..."
            />
            <button
              className="filter-btn"
              type="button"
              onClick={() => ccBccListService(reviewUserPage, reviewUserLimit, reviewUserSearch)}
              disabled={reviewUserSearch.length > 0 && reviewUserSearch.length < 3}
            >
              <i className="pi pi-search" />
            </button>
          </div>
        </div>
        <div className="invoice-review-email-table-wrap">
          <table className="invoice-review-email-table">
            <thead>
              <tr>
                <th>S.No.</th>
                <th>User ID</th>
                <th>Email</th>
                <th>Name</th>
                <th>Mobile No.</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {reviewUserLoading ? (
                <tr><td colSpan="6" className="invoice-review-empty">Loading...</td></tr>
              ) : reviewUserRows.length > 0 ? reviewUserRows.map((row, index) => (
                <tr key={row?.Code || row?.UserID || row?.EmailID || index}>
                  <td>{((reviewUserPage - 1) * reviewUserLimit) + index + 1}</td>
                  <td>{row?.UserID || row?.EmployeeCode || '---'}</td>
                  <td>{row?.EmailID || row?.Email || '---'}</td>
                  <td>{row?.FullName || row?.EmployeeName || row?.Name || '---'}</td>
                  <td>{row?.MobileNo || row?.MobileNumber || '---'}</td>
                  <td>
                    <button type="button" className="invoice-review-user-select" onClick={() => selectReviewUser(row)}>
                      Add
                    </button>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan="6" className="invoice-review-empty">No available options</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="invoice-review-user-footer">
          <div className="table-pagination">
            <button
              type="button"
              className="head-btn"
              disabled={reviewUserPage <= 1 || reviewUserLoading}
              onClick={() => changeReviewUserPage(reviewUserPage - 1)}
            >
              &laquo;
            </button>
            <button type="button" className="head-btn" disabled>{reviewUserPage || 1}</button>
            <button
              type="button"
              className="head-btn"
              disabled={!reviewUserTotalPages || reviewUserPage >= reviewUserTotalPages || reviewUserLoading}
              onClick={() => changeReviewUserPage(reviewUserPage + 1)}
            >
              &raquo;
            </button>
          </div>
          <button type="button" className="invoice-action-cancel" onClick={() => setReviewEmailDialog(false)}>Back</button>
        </div>
      </Dialog>

      <Dialog
        header={(
          <div className="invoice-approval-history-title">
            <span><i className="pi pi-history" /></span>
            <div>
              <strong>Approval History</strong>
              <small>Order #{value('PONumber', 'RequestNumber') || id}</small>
            </div>
          </div>
        )}
        visible={approvalHistoryVisible}
        draggable={false}
        className="invoice-approval-history-dialog"
        style={{ width: '520px' }}
        position="top"
        onHide={() => setApprovalHistoryVisible(false)}
        blockScroll
      >
        <div className="invoice-approval-timeline">
          {approvalHistoryLoading ? (
            <div className="invoice-approval-empty">Loading approval history...</div>
          ) : approvalHistoryRows.length > 0 ? approvalHistoryRows.map((row, index) => (
            <div className="invoice-approval-step" key={row?.WorkflowID || row?.RowNumber || index}>
              <div className={`invoice-approval-step-icon ${index === approvalHistoryRows.length - 1 ? 'pending' : ''}`}>
                <i className={`pi ${index === approvalHistoryRows.length - 1 ? 'pi-send' : 'pi-check'}`} />
              </div>
              <div className="invoice-approval-step-card">
                <div className="invoice-approval-step-top">
                  <span>{display(row?.DisplayStatus || row?.Status)}</span>
                  <time><i className="pi pi-calendar" /> {formatDateDisplay(row?.CreationDate)}</time>
                </div>
                <strong>{display(row?.UpdatedBy || row?.ApproverName || row?.CreatedBy)}</strong>
                {row?.Remarks ? (
                  <p>" {row.Remarks} "</p>
                ) : (
                  <em>No additional remarks provided at submission.</em>
                )}
              </div>
            </div>
          )) : (
            <div className="invoice-approval-empty">No approval history available</div>
          )}
        </div>
        <div className="invoice-approval-history-footer">
          <button type="button" onClick={() => setApprovalHistoryVisible(false)}>Close History</button>
        </div>
      </Dialog>

      <Dialog
        header="OCR Data"
        visible={jsonDialog}
        draggable={false}
        maximizable
        style={{ width: '75vw', height: 'auto' }}
        position="top"
        breakpoints={{ '960px': '75vw', '641px': '100vw' }}
        onHide={() => setJsonDialog(false)}
        blockScroll
      >
        <div className="data-table-body">
          <div className="ag-theme-quartz" style={{ height: 300, width: '100%', marginTop: '5px' }}>
            {ocrjsonData ? <JSONGrid data={ocrjsonData} /> : <div className="empty-table">No OCR data available</div>}
          </div>
        </div>
      </Dialog>

      <Dialog
        header={<h3 className="invoice-line-popup-title">{linePopup.title}</h3>}
        visible={linePopup.visible}
        draggable={false}
        className="invoice-line-popup-dialog"
        style={{ width: '460px' }}
        position="top"
        onHide={() => setLinePopup(prev => ({ ...prev, visible: false }))}
        blockScroll
      >
        <div className="invoice-line-popup-table-wrap">
          <table className="invoice-line-popup-table">
            <tbody>
              {linePopup.rows.map(([label, rowValue]) => (
                <tr key={label}>
                  <th>{label}</th>
                  <td>{display(rowValue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Dialog>

      <Dialog
        header={reportDialog.title}
        visible={reportDialog.visible}
        draggable={false}
        maximizable
        className="invoice-report-dialog"
        style={{ width: '90vw', maxWidth: '1440px' }}
        position="top"
        breakpoints={{ '960px': '75vw', '641px': '100vw' }}
        onHide={() => setReportDialog(prev => ({ ...prev, visible: false }))}
        blockScroll
      >
        {reportDialog.action === 'COMP_REPORT' && (
          <div className="invoice-report-meta">
            <div className="invoice-report-meta-item">
              <span>PO Number</span>
              <strong>{reportDialog?.data?.find(item => item?.PONumber)?.PONumber || ''}</strong>
            </div>
            <div className="invoice-report-meta-item">
              <span>Request ID</span>
              <strong>{reportDialog?.data?.[0]?.RequestNumber || value('RequestNumber') || ''}</strong>
            </div>
            <div className="invoice-report-meta-item">
              <span>Created By</span>
              <strong>{reportDialog?.data?.[0]?.CreatedBy || ''}</strong>
            </div>
            <div className="invoice-report-meta-item">
              <span>Created Date</span>
              <strong>{formatDateDisplay(reportDialog?.data?.[0]?.CreatedDate) || ''}</strong>
            </div>
          </div>
        )}
        <div className="invoice-report-toolbar">
          <div className="invoice-report-page-size">
            <span>Page Size:</span>
            <select
              value={reportDialog.limit}
              onChange={event => {
                const limit = Number(event.target.value);
                setReportDialog(prev => ({ ...prev, limit, page: 1 }));
                comparisionReport(1, limit, reportDialog.search, reportDialog.action);
              }}
            >
              {[10, 20, 50, 100, 200, 500, 1000].map(item => <option key={item} value={item}>{item}</option>)}
            </select>
            <span>entries</span>
          </div>
          <div className="invoice-report-search">
            <input
              type="search"
              className="table-search-data"
              value={reportDialog.search}
              onChange={event => setReportDialog(prev => ({ ...prev, search: event.target.value }))}
              placeholder="Search..."
            />
            <button
              className="filter-btn"
              type="button"
              onClick={() => comparisionReport(1, reportDialog.limit, reportDialog.search, reportDialog.action)}
              disabled={reportLoading || (reportDialog.search.length > 0 && reportDialog.search.length < 3)}
            >
              <i className="pi pi-search" />
            </button>
          </div>
          
        </div>
        <div className="invoice-report-table-wrap">
          <table className="invoice-report-table">
            <thead>
              <tr>
                {reportColumns.map(([label, key]) => (
                  <th key={key}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {reportLoading ? (
                <tr><td colSpan={reportColumns.length} className="empty-table">Loading...</td></tr>
              ) : reportDialog.data.length > 0 ? reportDialog.data.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {reportColumns.map(([, key, type]) => (
                    <td key={key}>{reportCellValue(row, key, type)}</td>
                  ))}
                </tr>
              )) : (
                <tr><td colSpan={reportColumns.length} className="empty-table">No data available</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="pagination-section invoice-report-pagination">
          <div className="pagination-entries">
            {reportDialog.totalRecords > 0 ? (
              <p style={{ color: 'black', fontSize: '13px' }}>
                Showing {reportDialog.data?.[0]?.RowNumber || 1} to {reportDialog.data?.[reportDialog.data.length - 1]?.RowNumber || reportDialog.data.length} of {reportDialog.totalRecords} entries
              </p>
            ) : ''}
          </div>
          <div className="table-pagination">
            <button
              type="button"
              className="head-btn"
              disabled={reportDialog.page <= 1 || reportLoading}
              onClick={() => comparisionReport(reportDialog.page - 1, reportDialog.limit, reportDialog.search, reportDialog.action)}
            >
              &laquo;
            </button>
            <button type="button" className="head-btn" disabled>{reportDialog.page || 1}</button>
            <button
              type="button"
              className="head-btn"
              disabled={reportDialog.page >= reportDialog.totalPages || reportLoading}
              onClick={() => comparisionReport(reportDialog.page + 1, reportDialog.limit, reportDialog.search, reportDialog.action)}
            >
              &raquo;
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
